import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/SimpleAuthContext';
import NotificationBell from '@/NotificationBell';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Calculator, Building, MapPin, Settings, BarChart3, DollarSign, Clock, PieChart, TrendingUp, Users, Info, AlertTriangle, TrendingDown, Shield, Plus, X, LogOut, User, CreditCard, HelpCircle, Menu, ChevronDown, HardHat, Hammer } from 'lucide-react';
import InfoTooltip from '@/components/InfoTooltip';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart as RechartsBarChart, Bar, ScatterChart, Scatter } from 'recharts';
import { toast } from 'sonner';
import ConstructionShell from '@/construction/ConstructionShell';
import '@/App.css';

// Ensure API URL always ends with /api
const getApiUrl = () => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  return baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
};
const BACKEND_URL = getApiUrl();
const API = BACKEND_URL;

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#8dd1e1', '#d084d0', '#ffb347', '#87ceeb'];

// Currency Configuration
const FOREX_RATES = {
  // Base currency
  USD: 1.0,
  
  // Americas
  CAD: 1.36,    // Canadian Dollar
  MXN: 17.05,   // Mexican Peso
  BRL: 4.97,    // Brazilian Real
  ARS: 350.00,  // Argentine Peso
  COP: 3925.00, // Colombian Peso
  CLP: 890.00,  // Chilean Peso
  PEN: 3.76,    // Peruvian Sol
  
  // Europe
  GBP: 0.79,    // British Pound
  EUR: 0.92,    // Euro
  CHF: 0.88,    // Swiss Franc
  NOK: 10.85,   // Norwegian Krone
  SEK: 10.40,   // Swedish Krona
  PLN: 3.97,    // Polish Zloty
  CZK: 22.50,   // Czech Koruna
  RON: 4.56,    // Romanian Leu
  UAH: 36.90,   // Ukrainian Hryvnia
  TRY: 32.15,   // Turkish Lira
  
  // Asia
  CNY: 7.24,    // Chinese Yuan
  INR: 83.12,   // Indian Rupee
  JPY: 149.50,  // Japanese Yen
  KRW: 1310.00, // South Korean Won
  SGD: 1.34,    // Singapore Dollar
  MYR: 4.68,    // Malaysian Ringgit
  THB: 35.40,   // Thai Baht
  PHP: 55.80,   // Philippine Peso
  VND: 24350.00,// Vietnamese Dong
  IDR: 15650.00,// Indonesian Rupiah
  PKR: 278.00,  // Pakistani Rupee
  BDT: 109.50,  // Bangladeshi Taka
  LKR: 326.00,  // Sri Lankan Rupee
  
  // Middle East
  AED: 3.67,    // UAE Dirham
  SAR: 3.75,    // Saudi Riyal
  QAR: 3.64,    // Qatari Riyal
  KWD: 0.31,    // Kuwaiti Dinar
  ILS: 3.73,    // Israeli Shekel
  
  // Oceania
  AUD: 1.52,    // Australian Dollar
  NZD: 1.65,    // New Zealand Dollar
  
  // Africa
  ZAR: 18.65,   // South African Rand
  EGP: 30.90,   // Egyptian Pound
  NGN: 775.00,  // Nigerian Naira
  KES: 129.00,  // Kenyan Shilling
  MAD: 9.95,    // Moroccan Dirham
  GHS: 12.05    // Ghanaian Cedi
};

// Country to Currency mapping (comprehensive for all 51 countries)
const COUNTRY_CURRENCY = {
  // Asia
  'India': 'INR',
  'China': 'CNY',
  'Philippines': 'PHP',
  'Vietnam': 'VND',
  'Malaysia': 'MYR',
  'Singapore': 'SGD',
  'Thailand': 'THB',
  'Indonesia': 'IDR',
  'Japan': 'JPY',
  'South Korea': 'KRW',
  'Pakistan': 'PKR',
  'Bangladesh': 'BDT',
  'Sri Lanka': 'LKR',
  
  // Europe
  'United Kingdom': 'GBP',
  'Poland': 'PLN',
  'Romania': 'RON',
  'Ukraine': 'UAH',
  'Portugal': 'EUR',
  'Spain': 'EUR',
  'Germany': 'EUR',
  'France': 'EUR',
  'Netherlands': 'EUR',
  'Italy': 'EUR',
  'Czech Republic': 'CZK',
  'Greece': 'EUR',
  'Ireland': 'EUR',
  'Sweden': 'SEK',
  'Norway': 'NOK',
  'Switzerland': 'CHF',
  
  // North America
  'United States': 'USD',
  'Canada': 'CAD',
  'Mexico': 'MXN',
  
  // South America
  'Brazil': 'BRL',
  'Argentina': 'ARS',
  'Colombia': 'COP',
  'Chile': 'CLP',
  'Peru': 'PEN',
  
  // Africa
  'South Africa': 'ZAR',
  'Egypt': 'EGP',
  'Nigeria': 'NGN',
  'Kenya': 'KES',
  'Morocco': 'MAD',
  'Ghana': 'GHS',
  
  // Oceania
  'Australia': 'AUD',
  'New Zealand': 'NZD',
  
  // Middle East
  'United Arab Emirates': 'AED',
  'Saudi Arabia': 'SAR',
  'Qatar': 'QAR',
  'Kuwait': 'KWD',
  'Israel': 'ILS',
  'Turkey': 'TRY'
};

const CURRENCY_SYMBOLS = {
  USD: '$',
  CAD: 'C$',
  MXN: 'Mex$',
  BRL: 'R$',
  ARS: 'ARS$',
  COP: 'COL$',
  CLP: 'CLP$',
  PEN: 'S/',
  
  GBP: '£',
  EUR: '€',
  CHF: 'CHF ',
  NOK: 'kr ',
  SEK: 'kr ',
  PLN: 'zł',
  CZK: 'Kč',
  RON: 'lei ',
  UAH: '₴',
  TRY: '₺',
  
  CNY: '¥',
  INR: '₹',
  JPY: '¥',
  KRW: '₩',
  SGD: 'S$',
  MYR: 'RM',
  THB: '฿',
  PHP: '₱',
  VND: '₫',
  IDR: 'Rp',
  PKR: '₨',
  BDT: '৳',
  LKR: 'Rs',
  
  AED: 'AED ',
  SAR: 'SAR ',
  QAR: 'QAR ',
  KWD: 'KWD ',
  ILS: '₪',
  
  AUD: 'A$',
  NZD: 'NZ$',
  
  ZAR: 'R',
  EGP: 'E£',
  NGN: '₦',
  KES: 'KSh',
  MAD: 'MAD ',
  GHS: 'GH₵'
};

// Get current currency from project country
const getCurrentCurrency = () => {
  const saved = localStorage.getItem('currentProject');
  if (saved) {
    const project = JSON.parse(saved);
    // Use project_country if available, fallback to legacy location
    const country = project.project_country || project.location;
    return COUNTRY_CURRENCY[country] || 'USD';
  }
  return 'USD';
};

// Convert from USD to target currency
const convertCurrency = (amountUSD, targetCurrency) => {
  const rate = FOREX_RATES[targetCurrency] || 1.0;
  return amountUSD * rate;
};

// Convert between any two currencies
const convertBetweenCurrencies = (amount, fromCurrency, toCurrency) => {
  // Convert to USD first, then to target currency
  const amountInUSD = amount / (FOREX_RATES[fromCurrency] || 1.0);
  return amountInUSD * (FOREX_RATES[toCurrency] || 1.0);
};

// Get currency for a specific country
const getCurrencyForCountry = (country) => {
  return COUNTRY_CURRENCY[country] || 'USD';
};

// Convert and format currency with symbol
const formatCurrencyWithSymbol = (amountUSD, targetCurrency = null) => {
  const currency = targetCurrency || getCurrentCurrency();
  const convertedAmount = convertCurrency(amountUSD, currency);
  const symbol = CURRENCY_SYMBOLS[currency] || '$';
  
  return `${symbol}${convertedAmount.toLocaleString(undefined, { 
    minimumFractionDigits: 0,
    maximumFractionDigits: 0 
  })}`;
};

// Format currency for rates (with decimals)
const formatRateWithSymbol = (amountUSD, targetCurrency = null) => {
  const currency = targetCurrency || getCurrentCurrency();
  const convertedAmount = convertCurrency(amountUSD, currency);
  const symbol = CURRENCY_SYMBOLS[currency] || '$';
  
  return `${symbol}${convertedAmount.toFixed(2)}`;
};

// Get currency symbol only
const getCurrencySymbol = (currency = null) => {
  const curr = currency || getCurrentCurrency();
  return CURRENCY_SYMBOLS[curr] || '$';
};

// Calculate weighted cost from multi-location allocations with forex conversion
const calculateMultiLocationCost = (baseRateUSD, multiLocationAllocations, resourceType) => {
  const projectCurrency = getCurrentCurrency();
  const allocations = multiLocationAllocations?.[resourceType] || [];
  
  if (allocations.length === 0) {
    // No multi-location, use base rate in project currency
    return convertCurrency(baseRateUSD, projectCurrency);
  }
  
  // Calculate weighted cost
  let totalWeightedCost = 0;
  
  allocations.forEach(allocation => {
    const { country, percentage } = allocation;
    if (!country || !percentage) return;
    
    // Get the country's currency
    const countryCurrency = getCurrencyForCountry(country);
    
    // Convert base USD rate to that country's currency, then back to project currency
    const rateInCountryCurrency = convertCurrency(baseRateUSD, countryCurrency);
    const rateInProjectCurrency = convertBetweenCurrencies(rateInCountryCurrency, countryCurrency, projectCurrency);
    
    // Apply percentage weight
    totalWeightedCost += (rateInProjectCurrency * (percentage / 100));
  });
  
  return totalWeightedCost || convertCurrency(baseRateUSD, projectCurrency);
};

// Format multi-location cost breakdown for display
const getMultiLocationBreakdown = (baseRateUSD, multiLocationAllocations, resourceType) => {
  const projectCurrency = getCurrentCurrency();
  const projectSymbol = getCurrencySymbol(projectCurrency);
  const allocations = multiLocationAllocations?.[resourceType] || [];
  
  if (allocations.length === 0) return null;
  
  const breakdown = allocations.map(allocation => {
    const { country, city, percentage } = allocation;
    if (!country || !percentage) return null;
    
    const countryCurrency = getCurrencyForCountry(country);
    const countrySymbol = getCurrencySymbol(countryCurrency);
    
    // Cost in local currency
    const localCost = convertCurrency(baseRateUSD, countryCurrency);
    
    // Cost in project currency
    const projectCost = convertBetweenCurrencies(localCost, countryCurrency, projectCurrency);
    
    return {
      location: `${city || ''}, ${country}`,
      percentage,
      localCost: `${countrySymbol}${localCost.toFixed(2)}`,
      projectCost: `${projectSymbol}${projectCost.toFixed(2)}`,
      weightedCost: projectCost * (percentage / 100)
    };
  }).filter(Boolean);
  
  return breakdown;
};

// Export & Print Utilities
const exportToPDF = (elementId, filename) => {
  const element = document.getElementById(elementId);
  if (!element) {
    toast.error('Unable to export. Please try again.');
    return;
  }
  
  window.print();
  toast.success('Print dialog opened');
};

const handlePrint = () => {
  window.print();
};

// Share calculator context across tabs
const getCurrentProjectContext = () => {
  const saved = localStorage.getItem('currentProject');
  return saved ? JSON.parse(saved) : null;
};

const getLatestCalculation = async () => {
  try {
    const response = await axios.get(`${API}/calculations`);
    return response.data.length > 0 ? response.data[0] : null;
  } catch (error) {
    return null;
  }
};

const ProjectCalculator = () => {
  const [formData, setFormData] = useState(() => {
    // Load from localStorage if available
    const saved = localStorage.getItem('currentProject');
    const defaultData = {
      project_name: '',
      structure_type: '',
      location: '', // Legacy field - kept for backward compatibility
      project_continent: '',
      project_country: '',
      project_city: '',
      complexity: '',
      lod_level: '',
      design_size: 1000,
      num_deliverables: 10,
      engineer_tech_split: {},
      project_start_date: '',
      project_end_date: '',
      roi_percentage: 25,
      breakeven_percentage: 60,
      percent_complete: 0,
      selected_currency: 'USD',
      selected_disciplines: [],
      resource_allocation: {
        engineers: '',
        technicians: '',
        managers: '',
        architects: '',
        specialists: ''
      },
      // New multi-location allocations
      multi_location_allocations: {},
      // Structure-specific specifications (new format)
      structure_specifications: null
    };
    
    // Merge saved data with defaults to ensure all fields exist
    if (saved) {
      const parsedData = JSON.parse(saved);
      return {
        ...defaultData,
        ...parsedData,
        multi_location_allocations: parsedData.multi_location_allocations || {},
        structure_specifications: parsedData.structure_specifications || null
      };
    }
    
    return defaultData;
  });
  
  const [structureSpecTemplate, setStructureSpecTemplate] = useState(null);
  
  const [showDisciplineSelector, setShowDisciplineSelector] = useState(false);
  const [showResourceAllocation, setShowResourceAllocation] = useState(false);
  const [optimizationSavings, setOptimizationSavings] = useState(null);

  const [locations, setLocations] = useState([]);
  const [continents, setContinents] = useState([]);
  const [hierarchicalLocations, setHierarchicalLocations] = useState({});
  const [disciplines, setDisciplines] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calculations, setCalculations] = useState([]);

  // Save to localStorage whenever formData changes
  useEffect(() => {
    localStorage.setItem('currentProject', JSON.stringify(formData));
  }, [formData]);
  
  // Fetch structure specifications when structure type changes
  useEffect(() => {
    const fetchStructureSpecs = async () => {
      if (formData.structure_type) {
        try {
          const response = await axios.get(`${API}/structure-specifications/${formData.structure_type}`);
          setStructureSpecTemplate(response.data);
          
          // Initialize structure specifications with defaults if not already set
          if (!formData.structure_specifications || !formData.structure_specifications[formData.structure_type]) {
            const defaults = {};
            response.data.fields.forEach(field => {
              defaults[field.key] = field.default;
            });
            setFormData(prev => ({
              ...prev,
              structure_specifications: {
                [formData.structure_type]: defaults
              }
            }));
          }
        } catch (error) {
          console.error('Failed to fetch structure specifications:', error);
          setStructureSpecTemplate(null);
        }
      } else {
        setStructureSpecTemplate(null);
      }
    };
    
    fetchStructureSpecs();
  }, [formData.structure_type]);

  const structureTypes = [
    // Transportation Infrastructure
    { value: 'station', label: '🚉 Station', category: 'Transportation' },
    { value: 'metro', label: '🚇 Metro', category: 'Transportation' },
    { value: 'rail', label: '🚄 Rail', category: 'Transportation' },
    { value: 'airport', label: '✈️ Airport', category: 'Transportation' },
    { value: 'tunnel', label: '🚇 Tunnel', category: 'Transportation' },
    { value: 'overground', label: '🚊 Overground', category: 'Transportation' },
    { value: 'bridge', label: '🌉 Bridge', category: 'Transportation' },
    { value: 'highway', label: '🛣️ Highway', category: 'Transportation' },
    { value: 'port', label: '⚓ Port/Harbor', category: 'Transportation' },
    { value: 'parking', label: '🅿️ Parking Structure', category: 'Transportation' },
    
    // Commercial Buildings
    { value: 'office', label: '🏢 Office Building', category: 'Commercial' },
    { value: 'retail', label: '🏪 Retail Store', category: 'Commercial' },
    { value: 'shopping_mall', label: '🛍️ Shopping Mall', category: 'Commercial' },
    { value: 'hotel', label: '🏨 Hotel', category: 'Commercial' },
    { value: 'mixed_use', label: '🏙️ Mixed-Use Development', category: 'Commercial' },
    
    // Residential
    { value: 'residential_low_rise', label: '🏘️ Residential Low-Rise (1-3 floors)', category: 'Residential' },
    { value: 'residential_mid_rise', label: '🏙️ Residential Mid-Rise (4-9 floors)', category: 'Residential' },
    { value: 'residential_high_rise', label: '🏢 Residential High-Rise (10+ floors)', category: 'Residential' },
    { value: 'apartment', label: '🏠 Apartment Complex', category: 'Residential' },
    
    // Industrial
    { value: 'warehouse', label: '🏭 Warehouse', category: 'Industrial' },
    { value: 'factory', label: '🏭 Factory/Manufacturing', category: 'Industrial' },
    { value: 'data_center', label: '💾 Data Center', category: 'Industrial' },
    { value: 'logistics', label: '📦 Logistics Center', category: 'Industrial' },
    
    // Healthcare & Education
    { value: 'hospital', label: '🏥 Hospital', category: 'Healthcare & Education' },
    { value: 'clinic', label: '⚕️ Clinic/Medical Center', category: 'Healthcare & Education' },
    { value: 'school', label: '🏫 School', category: 'Healthcare & Education' },
    { value: 'university', label: '🎓 University/College', category: 'Healthcare & Education' },
    { value: 'laboratory', label: '🔬 Laboratory/Research Facility', category: 'Healthcare & Education' },
    
    // Sports & Recreation
    { value: 'stadium', label: '🏟️ Stadium', category: 'Sports & Recreation' },
    { value: 'sports_complex', label: '⚽ Sports Complex', category: 'Sports & Recreation' },
    { value: 'recreation_center', label: '🎾 Recreation Center', category: 'Sports & Recreation' },
    
    // Other Specialized
    { value: 'museum', label: '🏛️ Museum/Cultural Center', category: 'Other' },
    { value: 'convention_center', label: '🎪 Convention Center', category: 'Other' },
    { value: 'power_plant', label: '⚡ Power Plant', category: 'Other' },
    { value: 'water_treatment', label: '💧 Water Treatment Plant', category: 'Other' }
  ];

  // Detailed structure configurations with sub-types and attributes
  const structureDetails = {
    // Transportation Infrastructure
    station: {
      attributes: ['num_floors', 'total_area'],
      subTypes: ['Platform Areas', 'Concourse', 'Ticketing Areas', 'Retail Spaces', 'Office Areas', 'Control Rooms', 'Back of House'],
      floorRange: { min: 1, max: 10, default: 2 }
    },
    metro: {
      attributes: ['num_floors', 'num_stations', 'tunnel_length'],
      subTypes: ['Underground Stations', 'Above Ground Stations', 'Tunnels', 'Depot/Maintenance', 'Control Center'],
      floorRange: { min: 1, max: 5, default: 2 }
    },
    airport: {
      attributes: ['num_floors', 'num_terminals'],
      subTypes: ['Terminal Buildings', 'Concourses', 'Airside Areas', 'Retail & F&B', 'Baggage Handling', 'Control Tower', 'Hangars', 'Cargo Facilities'],
      floorRange: { min: 2, max: 10, default: 3 }
    },
    hospital: {
      attributes: ['num_floors', 'num_beds'],
      subTypes: ['Emergency Department', 'Operating Rooms', 'ICU/CCU', 'Inpatient Wards', 'Outpatient Clinics', 'Diagnostic Imaging', 'Laboratory', 'Pharmacy', 'Administrative Areas'],
      floorRange: { min: 2, max: 20, default: 5 }
    },
    office: {
      attributes: ['num_floors', 'total_area'],
      subTypes: ['Open Office Areas', 'Private Offices', 'Meeting Rooms', 'Conference Rooms', 'Break Rooms', 'Reception/Lobby', 'Server Rooms', 'Storage'],
      floorRange: { min: 1, max: 100, default: 10 }
    },
    hotel: {
      attributes: ['num_floors', 'num_rooms'],
      subTypes: ['Guest Rooms', 'Suites', 'Lobby & Reception', 'Restaurants', 'Ballrooms', 'Meeting Rooms', 'Spa & Fitness', 'Kitchen & BOH', 'Parking'],
      floorRange: { min: 3, max: 80, default: 15 }
    },
    shopping_mall: {
      attributes: ['num_floors', 'total_area'],
      subTypes: ['Retail Units', 'Anchor Stores', 'Food Court', 'Entertainment Areas', 'Common Areas', 'Service Areas', 'Parking'],
      floorRange: { min: 1, max: 8, default: 3 }
    },
    residential_high_rise: {
      attributes: ['num_floors', 'num_units'],
      subTypes: ['Residential Units', 'Lobby', 'Amenity Spaces', 'Gym/Pool', 'Parking', 'Retail (Ground Floor)', 'Service Areas'],
      floorRange: { min: 10, max: 100, default: 25 }
    },
    residential_mid_rise: {
      attributes: ['num_floors', 'num_units'],
      subTypes: ['Residential Units', 'Lobby', 'Common Areas', 'Parking', 'Storage'],
      floorRange: { min: 4, max: 9, default: 6 }
    },
    residential_low_rise: {
      attributes: ['num_floors', 'num_units'],
      subTypes: ['Residential Units', 'Common Areas', 'Parking', 'Outdoor Spaces'],
      floorRange: { min: 1, max: 3, default: 2 }
    },
    warehouse: {
      attributes: ['num_floors', 'total_area'],
      subTypes: ['Storage Areas', 'Loading Docks', 'Office Areas', 'Mezzanine', 'Cold Storage'],
      floorRange: { min: 1, max: 3, default: 1 }
    },
    data_center: {
      attributes: ['num_floors', 'total_area'],
      subTypes: ['Server Rooms', 'Cooling Systems', 'Power Distribution', 'Office Areas', 'Loading Docks', 'Security Areas'],
      floorRange: { min: 1, max: 5, default: 2 }
    },
    factory: {
      attributes: ['num_floors', 'total_area'],
      subTypes: ['Production Areas', 'Assembly Lines', 'Warehouse', 'Office Areas', 'Quality Control', 'Loading Areas'],
      floorRange: { min: 1, max: 4, default: 1 }
    },
    school: {
      attributes: ['num_floors', 'num_classrooms'],
      subTypes: ['Classrooms', 'Laboratories', 'Library', 'Gymnasium', 'Cafeteria', 'Auditorium', 'Administrative Offices', 'Sports Facilities'],
      floorRange: { min: 1, max: 6, default: 3 }
    },
    university: {
      attributes: ['num_floors', 'num_buildings'],
      subTypes: ['Lecture Halls', 'Classrooms', 'Laboratories', 'Libraries', 'Dormitories', 'Cafeterias', 'Sports Facilities', 'Administration', 'Research Centers'],
      floorRange: { min: 2, max: 15, default: 5 }
    },
    stadium: {
      attributes: ['num_levels', 'seating_capacity'],
      subTypes: ['Seating Areas', 'VIP Boxes', 'Concessions', 'Restrooms', 'Locker Rooms', 'Media Areas', 'Parking'],
      floorRange: { min: 1, max: 8, default: 3 }
    },
    // Add defaults for other types
    default: {
      attributes: ['num_floors', 'total_area'],
      subTypes: ['Main Areas', 'Support Areas', 'Service Areas'],
      floorRange: { min: 1, max: 50, default: 3 }
    }
  };

  const complexityLevels = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' }
  ];

  const lodLevels = [
    { value: 'LOD200', label: 'LOD 200' },
    { value: 'LOD300', label: 'LOD 300' },
    { value: 'LOD400', label: 'LOD 400' },
    { value: 'LOD500', label: 'LOD 500' }
  ];

  // Enhanced resource categories for multi-location allocation
  const resourceCategories = [
    { key: 'senior_management', label: 'Senior Management', icon: '👔', desc: 'Directors, VPs, C-Level' },
    { key: 'project_managers', label: 'Project Managers', icon: '📊', desc: 'PM, PMO, Coordinators' },
    { key: 'principal_engineers', label: 'Principal Engineers', icon: '⭐', desc: 'Lead Engineers, Chiefs' },
    { key: 'senior_engineers', label: 'Senior Engineers', icon: '👨‍💼', desc: '5+ years experience' },
    { key: 'engineers', label: 'Engineers', icon: '💼', desc: '2-5 years experience' },
    { key: 'junior_engineers', label: 'Junior Engineers', icon: '👨‍💻', desc: '0-2 years experience' },
    { key: 'architects', label: 'Architects', icon: '🏛️', desc: 'Architects, Lead Architects' },
    { key: 'bim_modelers', label: 'BIM Modelers', icon: '🎨', desc: 'BIM Specialists, Coordinators' },
    { key: 'cad_drafters', label: 'CAD Drafters', icon: '✏️', desc: 'CAD Technicians, Drafters' },
    { key: 'senior_technicians', label: 'Senior Technicians', icon: '🔧', desc: '5+ years technical experience' },
    { key: 'technicians', label: 'Technicians', icon: '⚙️', desc: '2-5 years technical experience' },
    { key: 'qa_specialists', label: 'QA/QC Specialists', icon: '✅', desc: 'Quality Assurance, Quality Control' },
    { key: 'cost_estimators', label: 'Cost Estimators', icon: '💰', desc: 'Cost Engineers, Quantity Surveyors' },
    { key: 'surveyors', label: 'Surveyors', icon: '📐', desc: 'Land Surveyors, Site Surveyors' },
    { key: 'gis_specialists', label: 'GIS Specialists', icon: '🗺️', desc: 'GIS Analysts, Spatial Data Experts' }
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [locationsRes, disciplinesRes, calculationsRes, continentsRes, hierarchicalRes] = await Promise.all([
        axios.get(`${API}/locations`),
        axios.get(`${API}/disciplines`),
        axios.get(`${API}/calculations`),
        axios.get(`${API}/locations/continents`),
        axios.get(`${API}/locations/hierarchical`)
      ]);

      setLocations(locationsRes.data.locations);
      setDisciplines(disciplinesRes.data.disciplines);
      setCalculations(calculationsRes.data);
      setContinents(continentsRes.data.continents || []);
      setHierarchicalLocations(hierarchicalRes.data.hierarchy || {});

      // Initialize default engineer/tech splits
      const defaultSplits = {};
      disciplinesRes.data.disciplines.forEach(discipline => {
        defaultSplits[discipline] = 60; // 60% engineer, 40% technician
      });
      setFormData(prev => ({ ...prev, engineer_tech_split: defaultSplits }));
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast.error('Failed to load initial data');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // Auto-set currency when country changes
      if (field === 'project_country' && COUNTRY_CURRENCY[value]) {
        updated.selected_currency = COUNTRY_CURRENCY[value];
      }
      
      return updated;
    });
  };

  const clearProject = () => {
    const emptyProject = {
      project_name: '',
      structure_type: '',
      location: '',
      complexity: '',
      lod_level: '',
      design_size: 1000,
      num_deliverables: 10,
      engineer_tech_split: {},
      project_start_date: '',
      project_end_date: '',
      roi_percentage: 25,
      breakeven_percentage: 60,
      percent_complete: 0,
      selected_currency: 'USD'
    };
    setFormData(emptyProject);
    setResults(null);
    localStorage.removeItem('currentProject');
    toast.success('Project cleared successfully');
  };

  const handleSplitChange = (discipline, value) => {
    setFormData(prev => ({
      ...prev,
      engineer_tech_split: {
        ...prev.engineer_tech_split,
        [discipline]: value
      }
    }));
  };

  const calculateProject = async () => {
    if (!formData.project_name || !formData.structure_type || !formData.location || 
        !formData.complexity || !formData.lod_level || !formData.project_start_date || 
        !formData.project_end_date) {
      toast.error('Please fill in all required fields including project dates');
      return;
    }

    // Validate date range
    const startDate = new Date(formData.project_start_date);
    const endDate = new Date(formData.project_end_date);
    if (endDate <= startDate) {
      toast.error('Project end date must be after start date');
      return;
    }

    setLoading(true);
    try {
      // Format structure specifications for backend
      // The backend expects {generic: {structure_type: {field: value, ...}}}
      const calculationData = {
        ...formData,
        structure_specifications: formData.structure_specifications ? {
          generic: formData.structure_specifications
        } : null
      };
      
      const response = await axios.post(`${API}/calculate`, calculationData);
      setResults(response.data);
      toast.success('Project calculation completed successfully!');
      
      // Refresh calculations list
      const calculationsRes = await axios.get(`${API}/calculations`);
      setCalculations(calculationsRes.data);
    } catch (error) {
      console.error('Calculation error:', error);
      toast.error('Failed to calculate project effort');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return formatCurrencyWithSymbol(amount, formData.selected_currency);
  };

  const formatHours = (hours) => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 1
    }).format(hours);
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Quick Start Guide */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-3 sm:p-4 rounded-lg">
        <div className="flex items-start space-x-2 sm:space-x-3">
          <HelpCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-blue-900 mb-1 text-sm sm:text-base">📋 Quick Start Guide</h4>
            <p className="text-xs sm:text-sm text-blue-800 mb-2">
              Follow these steps to estimate your project effort and costs:
            </p>
            <ol className="text-xs sm:text-sm text-blue-700 space-y-1 ml-4 list-decimal">
              <li><strong>Basic Info:</strong> Enter project name, select structure type, and location</li>
              <li><strong>Parameters:</strong> Set complexity level, LOD, design size, and deliverables</li>
              <li><strong>Disciplines:</strong> Choose engineering disciplines required for your project</li>
              <li><strong>Optional:</strong> Configure financial targets and resource optimization for detailed analysis</li>
              <li><strong>Calculate:</strong> Click the Calculate button to generate your estimates</li>
            </ol>
            <p className="text-xs text-blue-600 mt-2">💡 Hover over any <HelpCircle className="h-3 w-3 inline" /> icon for detailed guidance</p>
          </div>
        </div>
      </div>

      {/* Input Form */}
      <Card className="shadow-lg border-0 bg-white/70 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="h-5 w-5 text-indigo-600" />
            <span>Project Configuration</span>
          </CardTitle>
          <CardDescription>
            Configure your project parameters to calculate effort and cost estimates. Required fields are marked with tooltips for guidance.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 sm:space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            <div className="space-y-2">
              <Label htmlFor="project_name">
                Project Name
                <InfoTooltip content="Give your project a unique, descriptive name for easy identification in your project list" />
              </Label>
              <Input
                id="project_name"
                data-testid="project-name-input"
                placeholder="E.g., Downtown Metro Station Phase 2"
                value={formData.project_name}
                onChange={(e) => handleInputChange('project_name', e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>
                Structure Type
                <InfoTooltip content="Select the type of structure for your project. This affects productivity multipliers and cost calculations based on industry standards" />
              </Label>
              <Select value={formData.structure_type} onValueChange={(value) => handleInputChange('structure_type', value)}>
                <SelectTrigger data-testid="structure-type-select">
                  <SelectValue placeholder="Select structure type" />
                </SelectTrigger>
                <SelectContent className="max-h-[400px]">
                  {Object.entries(
                    structureTypes.reduce((acc, type) => {
                      if (!acc[type.category]) acc[type.category] = [];
                      acc[type.category].push(type);
                      return acc;
                    }, {})
                  ).map(([category, types]) => (
                    <div key={category}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">
                        {category}
                      </div>
                      {types.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>
                Continent
                <InfoTooltip content="Select the continent where your project is located. This affects labor costs and productivity rates based on regional standards" />
              </Label>
              <Select 
                value={formData.project_continent} 
                onValueChange={(value) => {
                  handleInputChange('project_continent', value);
                  handleInputChange('project_country', '');
                  handleInputChange('project_city', '');
                  // Update legacy location field
                  handleInputChange('location', value);
                }}
              >
                <SelectTrigger data-testid="continent-select">
                  <SelectValue placeholder="Select continent" />
                </SelectTrigger>
                <SelectContent>
                  {continents.map((continent) => (
                    <SelectItem key={continent} value={continent}>
                      <div className="flex items-center space-x-2">
                        <MapPin className="h-4 w-4" />
                        <span>{continent}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Sub-Location Dropdowns (Country & City) */}
          {formData.project_continent && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-2">
                <Label>Country</Label>
                <Select 
                  value={formData.project_country} 
                  onValueChange={(value) => {
                    handleInputChange('project_country', value);
                    handleInputChange('project_city', '');
                  }}
                >
                  <SelectTrigger data-testid="country-select">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.project_continent && hierarchicalLocations[formData.project_continent] && 
                      Object.keys(hierarchicalLocations[formData.project_continent]).map((country) => (
                        <SelectItem key={country} value={country}>
                          <div className="flex items-center space-x-2">
                            <MapPin className="h-4 w-4" />
                            <span>{country}</span>
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>City</Label>
                <Select 
                  value={formData.project_city} 
                  onValueChange={(value) => handleInputChange('project_city', value)}
                  disabled={!formData.project_country}
                >
                  <SelectTrigger data-testid="city-select">
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.project_continent && formData.project_country && 
                      hierarchicalLocations[formData.project_continent]?.[formData.project_country]?.cities?.map((city) => (
                        <SelectItem key={city.name} value={city.name}>
                          <div className="flex items-center space-x-2">
                            <MapPin className="h-4 w-4" />
                            <span>{city.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Structure-Specific Specifications Panel */}
          {formData.structure_type && structureSpecTemplate && (
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 sm:p-6 rounded-lg border-2 border-blue-300">
              <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-4 flex items-center space-x-2">
                <Building className="h-5 w-5 text-blue-600" />
                <span>{structureSpecTemplate.label}</span>
                <InfoTooltip content="These specifications affect the calculation accuracy. Adjust values to match your project specifics." />
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {structureSpecTemplate.fields.map((field) => (
                  <div key={field.key} className="space-y-2">
                    <Label className="text-sm flex items-center space-x-1">
                      <span>{field.label}</span>
                      {field.tooltip && <InfoTooltip content={field.tooltip} />}
                    </Label>
                    
                    {field.type === 'number' && (
                      <Input
                        type="number"
                        min={field.min}
                        max={field.max}
                        value={formData.structure_specifications?.[formData.structure_type]?.[field.key] ?? field.default}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || field.default;
                          setFormData(prev => ({
                            ...prev,
                            structure_specifications: {
                              ...prev.structure_specifications,
                              [formData.structure_type]: {
                                ...prev.structure_specifications?.[formData.structure_type],
                                [field.key]: value
                              }
                            }
                          }));
                        }}
                        className="h-10"
                      />
                    )}
                    
                    {field.type === 'boolean' && (
                      <div className="flex items-center space-x-2 h-10">
                        <input
                          type="checkbox"
                          checked={formData.structure_specifications?.[formData.structure_type]?.[field.key] ?? field.default}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              structure_specifications: {
                                ...prev.structure_specifications,
                                [formData.structure_type]: {
                                  ...prev.structure_specifications?.[formData.structure_type],
                                  [field.key]: e.target.checked
                                }
                              }
                            }));
                          }}
                          className="w-5 h-5 text-blue-600 rounded"
                        />
                        <span className="text-sm text-slate-600">{field.default ? 'Yes' : 'No'} by default</span>
                      </div>
                    )}
                    
                    {field.type === 'select' && (
                      <Select
                        value={formData.structure_specifications?.[formData.structure_type]?.[field.key] ?? field.default}
                        onValueChange={(value) => {
                          setFormData(prev => ({
                            ...prev,
                            structure_specifications: {
                              ...prev.structure_specifications,
                              [formData.structure_type]: {
                                ...prev.structure_specifications?.[formData.structure_type],
                                [field.key]: value
                              }
                            }
                          }));
                        }}
                      >
                        <SelectTrigger className="h-10">
                          <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                        </SelectTrigger>
                        <SelectContent>
                          {field.options.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Specification Summary */}
              <div className="mt-4 p-3 bg-blue-100 rounded-lg">
                <p className="text-xs sm:text-sm text-blue-900">
                  <strong>Note:</strong> These specifications will adjust the effort calculations for disciplines most affected by {formData.structure_type} project parameters.
                </p>
              </div>
            </div>
          )}
          
          {/* Legacy Structure Details for non-supported types */}
          {formData.structure_type && !structureSpecTemplate && (
            <div className="bg-gradient-to-br from-gray-50 to-slate-50 p-4 sm:p-6 rounded-lg border-2 border-slate-300">
              <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-4 flex items-center space-x-2">
                <Building className="h-5 w-5 text-slate-600" />
                <span>Structure Details</span>
              </h3>
              <p className="text-sm text-slate-600">
                Detailed specifications for <strong>{formData.structure_type}</strong> structure type will use standard calculation parameters.
              </p>
            </div>
          )}

          {/* Project Parameters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
            <div className="space-y-2">
              <Label>
                Complexity Level
                <InfoTooltip content="Low: Standard designs with minimal customization. Medium: Moderate challenges with some custom elements. High: Complex designs with extensive coordination required" />
              </Label>
              <Select value={formData.complexity} onValueChange={(value) => handleInputChange('complexity', value)}>
                <SelectTrigger data-testid="complexity-select">
                  <SelectValue placeholder="Select complexity" />
                </SelectTrigger>
                <SelectContent>
                  {complexityLevels.map((level) => (
                    <SelectItem key={level.value} value={level.value}>
                      <Badge variant={level.value === 'low' ? 'secondary' : level.value === 'medium' ? 'default' : 'destructive'}>
                        {level.label}
                      </Badge>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>
                Level of Detail (LOD)
                <InfoTooltip content="LOD 100: Conceptual design. LOD 200: Schematic design. LOD 300: Detailed design. LOD 350: Construction documentation. Higher LOD = more detail and effort required" />
              </Label>
              <Select value={formData.lod_level} onValueChange={(value) => handleInputChange('lod_level', value)}>
                <SelectTrigger data-testid="lod-select">
                  <SelectValue placeholder="Select LOD" />
                </SelectTrigger>
                <SelectContent>
                  {lodLevels.map((lod) => (
                    <SelectItem key={lod.value} value={lod.value}>{lod.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="design_size">
                Design Size (m²)
                <InfoTooltip content="Total gross floor area or footprint of the project in square meters. This is used to calculate man-hours and costs" />
              </Label>
              <Input
                id="design_size"
                data-testid="design-size-input"
                type="number"
                value={formData.design_size}
                onChange={(e) => handleInputChange('design_size', parseFloat(e.target.value) || 0)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="num_deliverables">
                Number of Deliverables
                <InfoTooltip content="Total number of drawings, reports, models, or other deliverables expected for this project" />
              </Label>
              <Input
                id="num_deliverables"
                data-testid="deliverables-input"
                type="number"
                placeholder="E.g., 50"
                value={formData.num_deliverables}
                onChange={(e) => handleInputChange('num_deliverables', parseInt(e.target.value) || 0)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="project_start_date">
                Project Start Date
                <InfoTooltip content="The date when design work begins. Used for timeline planning and resource allocation" />
              </Label>
              <Input
                id="project_start_date"
                data-testid="start-date-input"
                type="date"
                value={formData.project_start_date}
                onChange={(e) => handleInputChange('project_start_date', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="project_end_date">
                Project End Date
                <InfoTooltip content="Target completion date for design deliverables. Must be after start date" />
              </Label>
              <Input
                id="project_end_date"
                data-testid="end-date-input"
                type="date"
                value={formData.project_end_date}
                onChange={(e) => handleInputChange('project_end_date', e.target.value)}
              />
            </div>
          </div>

          {/* Financial & Progress Parameters */}
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 p-6 rounded-lg border border-purple-200">
            <div className="flex items-center space-x-2 mb-4">
              <DollarSign className="h-5 w-5 text-purple-600" />
              <h3 className="text-lg font-semibold text-slate-900">Financial Targets & Progress Tracking</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
              <div className="space-y-2">
                <Label htmlFor="roi_percentage">
                  Target ROI %
                  <InfoTooltip content="The desired profit margin as a percentage of total project cost. For example, 20% means you want to make 20% profit over costs" />
                </Label>
                <div className="relative">
                  <Input
                    id="roi_percentage"
                    data-testid="roi-input"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    placeholder="E.g., 20"
                    value={formData.roi_percentage}
                    onChange={(e) => handleInputChange('roi_percentage', parseFloat(e.target.value) || 0)}
                    className="pr-8"
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500">%</span>
                </div>
                <p className="text-xs text-slate-600">Expected return on investment</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="breakeven_percentage">
                  Breakeven %
                  <InfoTooltip content="The percentage of project completion at which costs equal revenue. Typically 70-80% for design projects" />
                </Label>
                <div className="relative">
                  <Input
                    id="breakeven_percentage"
                    data-testid="breakeven-input"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    placeholder="E.g., 75"
                    value={formData.breakeven_percentage}
                    onChange={(e) => handleInputChange('breakeven_percentage', parseFloat(e.target.value) || 0)}
                    className="pr-8"
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500">%</span>
                </div>
                <p className="text-xs text-slate-600">Project completion at breakeven</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="percent_complete">
                  % Complete to Date
                  <InfoTooltip content="Current project completion percentage. Use this to track progress and remaining work" />
                </Label>
                <div className="relative">
                  <Input
                    id="percent_complete"
                    data-testid="percent-complete-input"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={formData.percent_complete}
                    onChange={(e) => handleInputChange('percent_complete', parseFloat(e.target.value) || 0)}
                    className="pr-8"
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500">%</span>
                </div>
                <p className="text-xs text-slate-600">Current project progress</p>
              </div>
            </div>
          </div>

          {/* Discipline Selection */}
          <div className="bg-gradient-to-br from-green-50 to-teal-50 p-6 rounded-lg border border-green-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Building className="h-5 w-5 text-green-600" />
                <h3 className="text-lg font-semibold text-slate-900">
                  Select Project Disciplines
                  <InfoTooltip content="Choose all engineering disciplines involved in your project. Each discipline has unique productivity rates and cost factors" side="right" />
                </h3>
                <Badge className="bg-green-100 text-green-800">
                  {(formData.selected_disciplines?.length || 0)} / {(disciplines?.length || 0)}
                </Badge>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDisciplineSelector(!showDisciplineSelector)}
              >
                {showDisciplineSelector ? 'Hide Disciplines' : 'Show All Disciplines'}
              </Button>
            </div>

            {showDisciplineSelector && disciplines && disciplines.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                {disciplines.map((discipline) => {
                  const isSelected = formData.selected_disciplines?.includes(discipline) || false;
                  return (
                    <div
                      key={discipline}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-green-500 bg-green-100'
                          : 'border-slate-200 bg-white hover:border-green-300'
                      }`}
                      onClick={() => {
                        const currentSelected = formData.selected_disciplines || [];
                        const newSelected = isSelected
                          ? currentSelected.filter(d => d !== discipline)
                          : [...currentSelected, discipline];
                        handleInputChange('selected_disciplines', newSelected);
                      }}
                    >
                      <div className="flex items-center space-x-2">
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            isSelected ? 'bg-green-500 border-green-500' : 'border-slate-300'
                          }`}
                        >
                          {isSelected && <span className="text-white text-xs">✓</span>}
                        </div>
                        <span className={`text-sm font-medium ${isSelected ? 'text-green-900' : 'text-slate-700'}`}>
                          {discipline}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-4 flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleInputChange('selected_disciplines', disciplines || [])}
                className="text-green-600 hover:text-green-700"
                disabled={!disciplines || disciplines.length === 0}
              >
                Select All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleInputChange('selected_disciplines', [])}
                className="text-red-600 hover:text-red-700"
              >
                Deselect All
              </Button>
            </div>

            {(!formData.selected_disciplines || formData.selected_disciplines.length === 0) && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                ⚠️ Please select at least one discipline to proceed with calculations
              </div>
            )}
          </div>

          {/* Regional Resource Optimization - Multi-Location with Percentage Allocation */}
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-6 rounded-lg border-2 border-purple-300">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-5 w-5 text-purple-600" />
                <h3 className="text-lg font-semibold text-slate-900">
                  Regional Resource Optimization
                  <InfoTooltip content="Optimize costs by distributing resources across different geographical regions. Mix high-skill/high-cost and lower-cost regions to balance quality and budget" side="right" />
                </h3>
                <Badge className="bg-purple-100 text-purple-800">
                  💰 Multi-Location Cost Optimization
                </Badge>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowResourceAllocation(!showResourceAllocation)}
              >
                {showResourceAllocation ? 'Hide Options' : 'Show Options'}
              </Button>
            </div>

            <p className="text-sm text-slate-600 mb-4">
              💡 <strong>Tip:</strong> Select multiple locations for each resource type with percentage allocation. Mix resources across regions to maximize profit while maintaining quality. Percentages must total 100% per resource.
            </p>

            {showResourceAllocation && continents && continents.length > 0 && (
              <div className="space-y-4 mt-4">
                {resourceCategories.map((resource) => {
                  const allocations = formData.multi_location_allocations?.[resource.key] || [];
                  const totalPercentage = allocations.reduce((sum, loc) => sum + (loc.percentage || 0), 0);
                  const isValid = totalPercentage === 0 || totalPercentage === 100;
                  
                  return (
                    <div key={resource.key} className="bg-white p-4 rounded-lg border-2 border-purple-200">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <span className="text-2xl">{resource.icon}</span>
                          <div>
                            <h4 className="font-semibold text-slate-900">{resource.label}</h4>
                            <p className="text-xs text-slate-500">{resource.desc}</p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newAllocations = { ...(formData.multi_location_allocations || {}) };
                            newAllocations[resource.key] = [
                              ...(newAllocations[resource.key] || []),
                              { continent: '', country: '', city: '', percentage: 0 }
                            ];
                            handleInputChange('multi_location_allocations', newAllocations);
                          }}
                          className="text-purple-600"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Location
                        </Button>
                      </div>

                      {allocations.length === 0 ? (
                        <div className="text-sm text-slate-500 italic py-2">
                          Using project location by default. Click "Add Location" to customize.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {allocations.map((allocation, idx) => (
                            <div key={idx} className="bg-slate-50 p-3 rounded border border-slate-200">
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
                                {/* Continent */}
                                <Select
                                  value={allocation.continent || ''}
                                  onValueChange={(value) => {
                                    const newAllocations = { ...(formData.multi_location_allocations || {}) };
                                    newAllocations[resource.key][idx].continent = value;
                                    newAllocations[resource.key][idx].country = '';
                                    newAllocations[resource.key][idx].city = '';
                                    handleInputChange('multi_location_allocations', newAllocations);
                                  }}
                                >
                                  <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Continent" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {continents.map((continent) => (
                                      <SelectItem key={continent} value={continent}>
                                        {continent}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>

                                {/* Country */}
                                <Select
                                  value={allocation.country || ''}
                                  disabled={!allocation.continent}
                                  onValueChange={(value) => {
                                    const newAllocations = { ...(formData.multi_location_allocations || {}) };
                                    newAllocations[resource.key][idx].country = value;
                                    newAllocations[resource.key][idx].city = '';
                                    handleInputChange('multi_location_allocations', newAllocations);
                                  }}
                                >
                                  <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Country" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {allocation.continent && hierarchicalLocations[allocation.continent] && 
                                      Object.keys(hierarchicalLocations[allocation.continent]).map((country) => (
                                        <SelectItem key={country} value={country}>
                                          {country}
                                        </SelectItem>
                                      ))}
                                  </SelectContent>
                                </Select>

                                {/* City */}
                                <Select
                                  value={allocation.city || ''}
                                  disabled={!allocation.country}
                                  onValueChange={(value) => {
                                    const newAllocations = { ...(formData.multi_location_allocations || {}) };
                                    newAllocations[resource.key][idx].city = value;
                                    handleInputChange('multi_location_allocations', newAllocations);
                                  }}
                                >
                                  <SelectTrigger className="w-full">
                                    <SelectValue placeholder="City" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {allocation.continent && allocation.country && 
                                      hierarchicalLocations[allocation.continent]?.[allocation.country]?.cities?.map((city) => (
                                        <SelectItem key={city.name} value={city.name}>
                                          {city.name}
                                        </SelectItem>
                                      ))}
                                  </SelectContent>
                                </Select>

                                {/* Percentage Input */}
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={allocation.percentage || 0}
                                    onChange={(e) => {
                                      const newAllocations = { ...(formData.multi_location_allocations || {}) };
                                      newAllocations[resource.key][idx].percentage = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                                      handleInputChange('multi_location_allocations', newAllocations);
                                    }}
                                    className="w-20 px-2 py-1 border border-slate-300 rounded text-sm"
                                  />
                                  <span className="text-sm text-slate-600">%</span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      const newAllocations = { ...(formData.multi_location_allocations || {}) };
                                      newAllocations[resource.key].splice(idx, 1);
                                      handleInputChange('multi_location_allocations', newAllocations);
                                    }}
                                    className="text-red-600 hover:text-red-700"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>

                              {/* Visual percentage bar */}
                              <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                                <div 
                                  className="bg-purple-600 h-2 transition-all"
                                  style={{ width: `${allocation.percentage || 0}%` }}
                                />
                              </div>
                            </div>
                          ))}

                          {/* Total Percentage Indicator */}
                          <div className={`p-2 rounded text-sm font-semibold ${
                            totalPercentage === 100 ? 'bg-green-100 text-green-800' :
                            totalPercentage === 0 ? 'bg-slate-100 text-slate-600' :
                            'bg-red-100 text-red-800'
                          }`}>
                            Total: {totalPercentage}% {!isValid && totalPercentage > 0 && '(Must equal 100%)'}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Optimization Summary */}
            {optimizationSavings && (
              <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-300">
                <div className="flex items-center space-x-3 mb-3">
                  <DollarSign className="h-6 w-6 text-green-600" />
                  <h4 className="font-semibold text-green-900">Estimated Optimization Benefits</h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                  <div className="bg-white p-3 rounded-lg border border-green-200">
                    <p className="text-xs text-green-600 mb-1">Baseline Cost (All Local)</p>
                    <p className="text-xl font-bold text-green-800">
                      {formatCurrencyWithSymbol(optimizationSavings.baselineCost)}
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-green-200">
                    <p className="text-xs text-green-600 mb-1">Optimized Cost (Mixed Regions)</p>
                    <p className="text-xl font-bold text-green-800">
                      {formatCurrencyWithSymbol(optimizationSavings.optimizedCost)}
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-green-200">
                    <p className="text-xs text-green-600 mb-1">Potential Savings</p>
                    <p className="text-xl font-bold text-green-700">
                      {formatCurrencyWithSymbol(optimizationSavings.savings)}
                    </p>
                    <Badge className="bg-green-600 text-white mt-1 text-xs">
                      {optimizationSavings.savingsPercent.toFixed(1)}% Reduction
                    </Badge>
                  </div>
                </div>
                <p className="text-sm text-green-700 mt-3">
                  💡 By allocating resources across regions, you can save{' '}
                  <strong>{formatCurrencyWithSymbol(optimizationSavings.savings)}</strong> on this project,
                  increasing profit margin by <strong>{optimizationSavings.savingsPercent.toFixed(1)}%</strong>
                </p>
              </div>
            )}

            {/* Quick Actions */}
            <div className="mt-4 flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  // Set all to most cost-effective regions (Asia for now)
                  const optimized = {
                    engineers: 'Asia',
                    technicians: 'Asia',
                    managers: formData.location || 'UK', // Keep managers local
                    architects: 'Europe',
                    specialists: 'Asia'
                  };
                  handleInputChange('resource_allocation', optimized);
                  toast.success('Applied cost-optimized allocation');
                }}
                className="text-purple-600 hover:text-purple-700"
              >
                💡 Apply Cost-Optimized Mix
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const reset = {
                    engineers: '',
                    technicians: '',
                    managers: '',
                    architects: '',
                    specialists: ''
                  };
                  handleInputChange('resource_allocation', reset);
                  setOptimizationSavings(null);
                  toast.info('Reset to project location');
                }}
                className="text-slate-600 hover:text-slate-700"
              >
                🔄 Reset to Project Location
              </Button>
            </div>
          </div>

          {/* Engineer/Technician Split */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-slate-900">
                Engineer/Technician Resource Split
                <InfoTooltip content="Adjust the ratio of engineers to technicians for each discipline. Engineers have higher hourly rates but more expertise. Typical split is 60% engineers, 40% technicians" side="right" />
              </h3>
              <Badge variant="outline">% Engineer</Badge>
              <Badge className="bg-blue-100 text-blue-800">
                {(formData.selected_disciplines?.length || 0) > 0 ? `${formData.selected_disciplines.length} Selected` : 'Select disciplines above'}
              </Badge>
            </div>
            <p className="text-sm text-slate-600">
              💡 <strong>Tip:</strong> Slide left for more technicians (lower cost), slide right for more engineers (higher expertise)
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {((formData.selected_disciplines?.length || 0) > 0 ? formData.selected_disciplines : disciplines || []).map((discipline) => (
                <div key={discipline} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label className="text-sm font-medium">{discipline}</Label>
                    <span className="text-sm text-slate-600">
                      {formData.engineer_tech_split[discipline] || 60}% Eng / {100 - (formData.engineer_tech_split[discipline] || 60)}% Tech
                    </span>
                  </div>
                  <Slider
                    value={[formData.engineer_tech_split[discipline] || 60]}
                    onValueChange={(value) => handleSplitChange(discipline, value[0])}
                    max={100}
                    min={0}
                    step={5}
                    className="w-full"
                    data-testid={`${discipline.toLowerCase().replace(/\s+/g, '-')}-split-slider`}
                  />
                </div>
              ))}
            </div>
          </div>

          <Separator />

          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <Button 
                onClick={clearProject}
                variant="outline"
                size="lg"
                className="px-6 text-slate-600 hover:text-red-600 hover:border-red-300"
              >
                Clear Project
              </Button>
              {formData.selected_currency && formData.selected_currency !== 'USD' && (
                <Badge variant="outline" className="text-sm">
                  Currency: {formData.selected_currency}
                </Badge>
              )}
            </div>
            <Button 
              onClick={calculateProject} 
              disabled={loading}
              size="lg"
              className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="calculate-button"
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Calculating...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Calculator className="h-4 w-4" />
                  <span>Calculate Project Effort</span>
                </div>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results Section */}
      {results && (
        <Card className="shadow-lg border-0 bg-white/70 backdrop-blur-sm" id="calculator-results">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center space-x-2">
                  <BarChart3 className="h-5 w-5 text-indigo-600" />
                  <span>Project Effort Calculation Results</span>
                </CardTitle>
                <CardDescription>
                  Detailed breakdown of effort and cost estimates for "{results.project_name}"
                </CardDescription>
              </div>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm" onClick={handlePrint}>
                  <span className="mr-2">🖨️</span> Print
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportToPDF('calculator-results', 'project-calculation.pdf')}>
                  <span className="mr-2">📄</span> Export PDF
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-8">
              <div className="p-6 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl text-white">
                <div className="flex items-center space-x-3">
                  <Clock className="h-8 w-8" />
                  <div>
                    <p className="text-indigo-100 text-sm">Total Project Hours</p>
                    <p className="text-3xl font-bold" data-testid="total-hours">{formatHours(results.total_hours)}</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <DollarSign className="h-8 w-8" />
                    <div>
                      <p className="text-emerald-100 text-sm">Total Project Cost</p>
                      <p className="text-3xl font-bold" data-testid="total-cost">{formatCurrency(results.total_cost_usd)}</p>
                    </div>
                  </div>
                  {formData.selected_currency && formData.selected_currency !== 'USD' && (
                    <Badge className="bg-white/20 text-white border-white/30">
                      {formData.selected_currency}
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Discipline Breakdown */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Discipline Breakdown</h3>
              <p className="text-xs text-slate-500 sm:hidden mb-2">← Scroll horizontally to see all columns →</p>
              <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
                <table className="w-full border-collapse min-w-[700px]">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left p-3 sm:p-4 font-semibold text-slate-900 whitespace-nowrap">Discipline</th>
                      <th className="text-right p-3 sm:p-4 font-semibold text-slate-900 whitespace-nowrap">Total Hours</th>
                      <th className="text-right p-3 sm:p-4 font-semibold text-slate-900 whitespace-nowrap">Engineer Hours</th>
                      <th className="text-right p-3 sm:p-4 font-semibold text-slate-900 whitespace-nowrap">Tech Hours</th>
                      <th className="text-right p-3 sm:p-4 font-semibold text-slate-900 whitespace-nowrap">Total Cost</th>
                      <th className="text-right p-3 sm:p-4 font-semibold text-slate-900 whitespace-nowrap">Units</th>
                      <th className="text-center p-3 sm:p-4 font-semibold text-slate-900 whitespace-nowrap">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.discipline_breakdown.map((discipline, index) => (
                      <DisciplineRow key={index} discipline={discipline} formatCurrency={formatCurrency} formatHours={formatHours} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
};

const DisciplineRow = ({ discipline, formatCurrency, formatHours }) => {
  const [disciplineDetails, setDisciplineDetails] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleDetailsClick = async () => {
    try {
      const response = await axios.get(`${API}/discipline/${encodeURIComponent(discipline.discipline)}`);
      setDisciplineDetails(response.data);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error fetching discipline details:', error);
      toast.error('Failed to load discipline details');
    }
  };

  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
        <td className="p-4 font-medium text-slate-900" data-testid={`discipline-${discipline.discipline.toLowerCase().replace(/\s+/g, '-')}`}>
          {discipline.discipline}
        </td>
        <td className="p-4 text-right text-slate-700" data-testid={`hours-${discipline.discipline.toLowerCase().replace(/\s+/g, '-')}`}>
          {formatHours(discipline.total_hours)}
        </td>
        <td className="p-4 text-right text-slate-600">
          {formatHours(discipline.engineer_hours)}
        </td>
        <td className="p-4 text-right text-slate-600">
          {formatHours(discipline.technician_hours)}
        </td>
        <td className="p-4 text-right font-semibold text-slate-900" data-testid={`cost-${discipline.discipline.toLowerCase().replace(/\s+/g, '-')}`}>
          {formatCurrency(discipline.total_cost_usd)}
        </td>
        <td className="p-4 text-right text-slate-600">
          {formatHours(discipline.unit_count)}
        </td>
        <td className="p-4 text-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDetailsClick}
            data-testid={`details-${discipline.discipline.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <Info className="h-4 w-4" />
          </Button>
        </td>
      </tr>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{discipline.discipline} - Detailed Summary</DialogTitle>
            <DialogDescription>
              Comprehensive breakdown and assumptions for this discipline
            </DialogDescription>
          </DialogHeader>
          {disciplineDetails && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-blue-600">Total Hours</p>
                  <p className="text-2xl font-bold text-blue-900">{formatHours(discipline.total_hours)}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-green-600">Total Cost</p>
                  <p className="text-2xl font-bold text-green-900">{formatCurrency(discipline.total_cost_usd)}</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-purple-600">Engineer Hours</p>
                  <p className="text-2xl font-bold text-purple-900">{formatHours(discipline.engineer_hours)}</p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-orange-600">Tech Hours</p>
                  <p className="text-2xl font-bold text-orange-900">{formatHours(discipline.technician_hours)}</p>
                </div>
              </div>

              {/* Discipline Info */}
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Unit Type</h4>
                  <p className="text-slate-700">{disciplineDetails.discipline.unit_type}</p>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Base Productivity</h4>
                  <p className="text-slate-700">{disciplineDetails.discipline.base_hours_per_unit} hours {disciplineDetails.discipline.unit_type}</p>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Key Assumptions</h4>
                  <ul className="list-disc list-inside space-y-1 text-slate-700">
                    {disciplineDetails.assumptions.map((assumption, index) => (
                      <li key={index}>{assumption}</li>
                    ))}
                  </ul>
                </div>

                {/* Multipliers */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Structure Type Multipliers</h4>
                    <div className="space-y-1 text-sm">
                      {Object.entries(disciplineDetails.discipline.structure_type_multiplier).map(([type, mult]) => (
                        <div key={type} className="flex justify-between">
                          <span className="capitalize">{type}:</span>
                          <span>{mult}x</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Complexity Multipliers</h4>
                    <div className="space-y-1 text-sm">
                      {Object.entries(disciplineDetails.discipline.complexity_multiplier).map(([level, mult]) => (
                        <div key={level} className="flex justify-between">
                          <span className="capitalize">{level}:</span>
                          <span>{mult}x</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">LOD Multipliers</h4>
                    <div className="space-y-1 text-sm">
                      {Object.entries(disciplineDetails.discipline.lod_multiplier).map(([lod, mult]) => (
                        <div key={lod} className="flex justify-between">
                          <span>{lod}:</span>
                          <span>{mult}x</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const AnalyticsDashboard = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedDiscipline, setSelectedDiscipline] = useState(null);
  const [showDetailedView, setShowDetailedView] = useState(false);
  const [calculations, setCalculations] = useState([]);

  useEffect(() => {
    fetchAnalyticsData();
    fetchDetailedCalculations();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      const response = await axios.get(`${API}/analytics/summary`);
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDetailedCalculations = async () => {
    try {
      const response = await axios.get(`${API}/calculations`);
      setCalculations(response.data);
    } catch (error) {
      console.error('Error fetching calculations:', error);
    }
  };

  const handleDisciplineClick = (discipline) => {
    setSelectedDiscipline(discipline);
    setShowDetailedView(true);
  };

  const getEfficiencyMetrics = () => {
    if (!analyticsData || !analyticsData.cost_trends.length) return {};
    
    const costs = analyticsData.cost_trends.map(trend => trend.total_cost);
    const hours = analyticsData.cost_trends.map(trend => trend.total_hours);
    
    const avgCostPerHour = costs.reduce((sum, cost, i) => sum + (cost / hours[i]), 0) / costs.length;
    const totalCost = costs.reduce((sum, cost) => sum + cost, 0);
    const totalHours = hours.reduce((sum, hour) => sum + hour, 0);
    
    return {
      avgCostPerHour: avgCostPerHour || 0,
      totalCost,
      totalHours,
      efficiency: totalHours > 0 ? (totalCost / totalHours) : 0
    };
  };

  const getRegionalAnalysis = () => {
    if (!calculations.length) return [];
    
    const regions = {};
    calculations.forEach(calc => {
      const region = calc.location || 'Unknown';
      if (!regions[region]) {
        regions[region] = { projects: 0, totalCost: 0, totalHours: 0 };
      }
      regions[region].projects += 1;
      regions[region].totalCost += calc.total_cost_usd || 0;
      regions[region].totalHours += calc.total_hours || 0;
    });
    
    return Object.entries(regions).map(([region, data]) => ({
      region,
      projects: data.projects,
      avgCost: data.totalCost / data.projects,
      avgHours: data.totalHours / data.projects,
      costPerHour: data.totalHours > 0 ? data.totalCost / data.totalHours : 0
    }));
  };

  const getComplexityInsights = () => {
    if (!calculations.length) return { low: 0, medium: 0, high: 0 };
    
    const complexity = { low: 0, medium: 0, high: 0 };
    calculations.forEach(calc => {
      const level = calc.complexity || 'medium';
      complexity[level] = (complexity[level] || 0) + 1;
    });
    
    return complexity;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!analyticsData || analyticsData.total_projects === 0) {
    return (
      <Card className="text-center p-8">
        <CardContent>
          <BarChart3 className="h-12 w-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Data Available</h3>
          <p className="text-slate-600">Calculate some projects first to see analytics</p>
        </CardContent>
      </Card>
    );
  }

  const efficiencyMetrics = getEfficiencyMetrics();
  const regionalAnalysis = getRegionalAnalysis();
  const complexityInsights = getComplexityInsights();

  return (
    <div className="space-y-8" id="analytics-dashboard">
      {/* Header with Export Options */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Projects Analytics</CardTitle>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <span className="mr-2">🖨️</span> Print
              </Button>
              <Button variant="outline" size="sm" onClick={() => exportToPDF('analytics-dashboard', 'analytics-report.pdf')}>
                <span className="mr-2">📄</span> Export PDF
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Filter Controls */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2">
              <Label>Filter by:</Label>
              <Select value={selectedFilter} onValueChange={setSelectedFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  <SelectItem value="station">Station</SelectItem>
                  <SelectItem value="metro">Metro</SelectItem>
                  <SelectItem value="rail">Rail</SelectItem>
                  <SelectItem value="airport">Airport</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDetailedView(!showDetailedView)}
            >
              {showDetailedView ? 'Summary View' : 'Detailed View'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Enhanced Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BarChart3 className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Projects</p>
                <p className="text-2xl font-bold text-slate-900">{analyticsData.total_projects}</p>
                <p className="text-xs text-green-600">+12% vs last period</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Value</p>
                <p className="text-2xl font-bold text-slate-900">
                  ${(analyticsData.total_value / 1000000).toFixed(1)}M
                </p>
                <p className="text-xs text-slate-500">
                  Avg: ${(analyticsData.avg_project_cost / 1000).toFixed(0)}K
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Efficiency Rate</p>
                <p className="text-2xl font-bold text-slate-900">
                  ${analyticsData.efficiency_rate.toFixed(0)}/hr
                </p>
                <p className="text-xs text-blue-600">Average project rate</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Complexity Distribution</p>
                <div className="flex space-x-2 mt-1">
                  <Badge variant="secondary" className="text-xs">H: {complexityInsights.high}</Badge>
                  <Badge variant="default" className="text-xs">M: {complexityInsights.medium}</Badge>
                  <Badge variant="outline" className="text-xs">L: {complexityInsights.low}</Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Interactive Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Enhanced Discipline Distribution */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <PieChart className="h-5 w-5" />
                <span>Discipline Cost Distribution</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDetailedView(!showDetailedView)}
              >
                <Info className="h-4 w-4" />
              </Button>
            </CardTitle>
            <CardDescription>Click any segment for detailed breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsPieChart>
                <Pie
                  data={analyticsData.discipline_distribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  onClick={(data) => handleDisciplineClick(data.name)}
                  style={{ cursor: 'pointer' }}
                >
                  {analyticsData.discipline_distribution.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={COLORS[index % COLORS.length]}
                      stroke={selectedDiscipline === entry.name ? "#000" : "none"}
                      strokeWidth={selectedDiscipline === entry.name ? 2 : 0}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Cost']} />
              </RechartsPieChart>
            </ResponsiveContainer>
            {showDetailedView && (
              <div className="mt-4 space-y-2">
                {analyticsData.discipline_distribution.map((discipline, index) => (
                  <div key={discipline.name} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                    <div className="flex items-center space-x-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-sm font-medium">{discipline.name}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">${discipline.value.toLocaleString()}</p>
                      <p className="text-xs text-slate-500">{discipline.hours.toFixed(0)} hrs</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Enhanced Structure Usage with Histogram */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Building className="h-5 w-5" />
              <span>Structure Type Analysis</span>
            </CardTitle>
            <CardDescription>Project distribution and utilization rates</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsBarChart data={analyticsData.structure_type_distribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip 
                  formatter={(value, name) => [`${value} projects`, 'Count']}
                  labelFormatter={(label) => `Structure: ${label}`}
                />
                <Bar 
                  dataKey="value" 
                  fill="#8884d8"
                  radius={[4, 4, 0, 0]}
                  onClick={(data) => toast.info(`${data.name}: ${data.value} projects`)}
                  style={{ cursor: 'pointer' }}
                />
              </RechartsBarChart>
            </ResponsiveContainer>
            {showDetailedView && (
              <div className="mt-4 grid grid-cols-2 gap-2">
                {analyticsData.structure_type_distribution.map((type, index) => (
                  <div key={type.name} className="p-2 bg-slate-50 rounded text-center">
                    <p className="text-sm font-medium">{type.name}</p>
                    <p className="text-lg font-bold text-indigo-600">{type.value}</p>
                    <p className="text-xs text-slate-500">
                      {((type.value / analyticsData.total_projects) * 100).toFixed(1)}%
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Enhanced Monthly Trends */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5" />
              <span>Monthly Performance Trends</span>
            </CardTitle>
            <CardDescription>Cost, hours, and project volume over time</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analyticsData.cost_trends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip 
                  formatter={(value, name) => [
                    name.includes('cost') ? `$${value.toLocaleString()}` : value,
                    name === 'total_cost' ? 'Total Cost' : 
                    name === 'avg_cost' ? 'Avg Cost' : 
                    name === 'projects' ? 'Projects' : 'Total Hours'
                  ]}
                />
                <Legend />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="total_cost" 
                  stroke="#8884d8" 
                  name="Total Cost"
                  strokeWidth={2}
                  dot={{ fill: '#8884d8', strokeWidth: 2, r: 4 }}
                />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="avg_cost" 
                  stroke="#82ca9d" 
                  name="Avg Cost"
                  strokeWidth={2}
                  dot={{ fill: '#82ca9d', strokeWidth: 2, r: 4 }}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="projects" 
                  stroke="#ffc658" 
                  name="Projects"
                  strokeWidth={2}
                  dot={{ fill: '#ffc658', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Enhanced Complexity Analysis */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5" />
              <span>Complexity vs Performance</span>
            </CardTitle>
            <CardDescription>Cost efficiency across complexity levels</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <ScatterChart data={analyticsData.complexity_analysis}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  type="category"
                  dataKey="complexity"
                  name="Complexity"
                />
                <YAxis 
                  type="number"
                  dataKey="cost"
                  name="Cost"
                  tickFormatter={(value) => `$${(value/1000).toFixed(0)}K`}
                />
                <Tooltip 
                  cursor={{ strokeDasharray: '3 3' }}
                  formatter={(value, name) => [
                    name === 'cost' ? `$${value.toLocaleString()}` : value,
                    name === 'cost' ? 'Cost' : 'Hours'
                  ]}
                  labelFormatter={(label) => `Complexity: ${label}`}
                />
                <Scatter 
                  name="Projects" 
                  dataKey="cost" 
                  fill="#8884d8"
                  onClick={(data) => toast.info(`${data.complexity} complexity: $${data.cost.toLocaleString()}`)}
                  style={{ cursor: 'pointer' }}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Regional Analysis */}
      {regionalAnalysis.length > 0 && (
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <MapPin className="h-5 w-5" />
              <span>Regional Performance Analysis</span>
            </CardTitle>
            <CardDescription>Cost efficiency and project distribution by region</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
              <table className="w-full border-collapse min-w-[600px]">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="text-left p-4 font-semibold">Region</th>
                    <th className="text-center p-4 font-semibold">Projects</th>
                    <th className="text-right p-4 font-semibold">Avg Cost</th>
                    <th className="text-right p-4 font-semibold">Avg Hours</th>
                    <th className="text-right p-4 font-semibold">Cost/Hour</th>
                    <th className="text-center p-4 font-semibold">Efficiency</th>
                  </tr>
                </thead>
                <tbody>
                  {regionalAnalysis.map((region, index) => (
                    <tr 
                      key={region.region} 
                      className="border-b hover:bg-slate-50/50 cursor-pointer transition-colors"
                      onClick={() => toast.info(`${region.region}: ${region.projects} projects analyzed`)}
                    >
                      <td className="p-4 font-medium">{region.region}</td>
                      <td className="text-center p-4">{region.projects}</td>
                      <td className="text-right p-4">${region.avgCost.toLocaleString()}</td>
                      <td className="text-right p-4">{region.avgHours.toLocaleString()}</td>
                      <td className="text-right p-4">${region.costPerHour.toFixed(0)}</td>
                      <td className="text-center p-4">
                        <Badge variant={region.costPerHour < 75 ? 'default' : region.costPerHour < 100 ? 'secondary' : 'destructive'}>
                          {region.costPerHour < 75 ? 'High' : region.costPerHour < 100 ? 'Medium' : 'Low'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detailed Project Analysis Table */}
      {showDetailedView && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Settings className="h-5 w-5" />
              <span>Detailed Project Analysis</span>
            </CardTitle>
            <CardDescription>
              Complete breakdown of all projects with drill-down capabilities
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
              <table className="w-full border-collapse min-w-[600px]">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="text-left p-3 font-semibold">Project</th>
                    <th className="text-center p-3 font-semibold">Structure</th>
                    <th className="text-center p-3 font-semibold">Complexity</th>
                    <th className="text-center p-3 font-semibold">Location</th>
                    <th className="text-right p-3 font-semibold">Total Cost</th>
                    <th className="text-right p-3 font-semibold">Total Hours</th>
                    <th className="text-right p-3 font-semibold">Cost/Hour</th>
                    <th className="text-center p-3 font-semibold">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {calculations.slice(0, 10).map((calc, index) => (
                    <tr 
                      key={index} 
                      className="border-b hover:bg-slate-50/50 cursor-pointer transition-colors"
                      onClick={() => toast.info(`Analyzing ${calc.project_name}`)}
                    >
                      <td className="p-3 font-medium text-sm">{calc.project_name}</td>
                      <td className="text-center p-3">
                        <Badge variant="outline" className="text-xs">
                          {calc.structure_type || 'N/A'}
                        </Badge>
                      </td>
                      <td className="text-center p-3">
                        <Badge variant={
                          calc.complexity === 'low' ? 'secondary' : 
                          calc.complexity === 'medium' ? 'default' : 'destructive'
                        } className="text-xs">
                          {calc.complexity || 'Medium'}
                        </Badge>
                      </td>
                      <td className="text-center p-3 text-sm">{calc.location || 'Unknown'}</td>
                      <td className="text-right p-3 font-semibold">
                        ${calc.total_cost_usd ? calc.total_cost_usd.toLocaleString() : '0'}
                      </td>
                      <td className="text-right p-3">
                        {calc.total_hours ? calc.total_hours.toLocaleString() : '0'}
                      </td>
                      <td className="text-right p-3">
                        ${calc.total_hours > 0 ? (calc.total_cost_usd / calc.total_hours).toFixed(0) : '0'}
                      </td>
                      <td className="text-center p-3 text-xs text-slate-500">
                        {new Date(calc.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {calculations.length > 10 && (
              <div className="mt-4 text-center">
                <Button variant="outline" size="sm">
                  Load More ({calculations.length - 10} remaining)
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Discipline Detail Modal */}
      <Dialog open={showDetailedView && selectedDiscipline} onOpenChange={() => setSelectedDiscipline(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{selectedDiscipline} - Detailed Analysis</DialogTitle>
            <DialogDescription>
              Comprehensive breakdown and performance metrics
            </DialogDescription>
          </DialogHeader>
          {selectedDiscipline && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-blue-600">Total Projects</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {analyticsData.discipline_distribution.find(d => d.name === selectedDiscipline)?.projects || 'N/A'}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-green-600">Total Cost</p>
                  <p className="text-2xl font-bold text-green-900">
                    ${analyticsData.discipline_distribution.find(d => d.name === selectedDiscipline)?.value.toLocaleString() || '0'}
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-purple-600">Total Hours</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {analyticsData.discipline_distribution.find(d => d.name === selectedDiscipline)?.hours.toFixed(0) || '0'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Recent Calculations */}
      {calculations.length > 0 && (
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Clock className="h-5 w-5 text-indigo-600" />
              <span>Recent Project Calculations</span>
            </CardTitle>
            <CardDescription>Last 10 calculated projects with details</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {calculations.slice(0, 10).map((calc, index) => (
                <div key={index} className="flex justify-between items-center p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-lg hover:shadow-md transition-shadow border border-slate-200">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <Badge variant="outline" className="bg-blue-50">#{index + 1}</Badge>
                      <h4 className="font-semibold text-slate-900">{calc.project_name}</h4>
                    </div>
                    <div className="mt-2 flex items-center space-x-4 text-xs text-slate-600">
                      <span className="flex items-center space-x-1">
                        <MapPin className="h-3 w-3" />
                        <span>{calc.location}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Building className="h-3 w-3" />
                        <span className="capitalize">{calc.structure_type}</span>
                      </span>
                      <span>{new Date(calc.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="text-right ml-6">
                    <p className="text-lg font-bold text-indigo-600">${(calc.total_cost_usd / 1000).toFixed(0)}K</p>
                    <p className="text-sm text-slate-600">{calc.total_hours.toFixed(0)} hrs</p>
                    {calc.financial_metrics && (
                      <Badge className="mt-1 bg-green-100 text-green-800 text-xs">
                        ROI: {calc.financial_metrics.roi_percentage}%
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const ProjectAnalysis = () => {
  const [calculations, setCalculations] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projectDetails, setProjectDetails] = useState(null);
  const [disciplineComparison, setDisciplineComparison] = useState([]);
  const [selectedDisciplineFilter, setSelectedDisciplineFilter] = useState('all');
  const [monthlyProfitData, setMonthlyProfitData] = useState([]);

  useEffect(() => {
    fetchCalculations();
  }, []);

  const fetchCalculations = async () => {
    try {
      const response = await axios.get(`${API}/calculations`);
      setCalculations(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0]);
        analyzeProject(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching calculations:', error);
      toast.error('Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  const analyzeProject = (project) => {
    if (!project) return;

    setProjectDetails(project);
    
    // Create discipline comparison data
    const disciplineData = project.discipline_breakdown.map((discipline, index) => ({
      name: discipline.discipline,
      totalHours: discipline.total_hours,
      engineerHours: discipline.engineer_hours,
      technicianHours: discipline.technician_hours,
      totalCost: discipline.total_cost_usd,
      engineerCost: discipline.engineer_cost_usd,
      technicianCost: discipline.technician_cost_usd,
      efficiency: discipline.total_hours > 0 ? (discipline.total_cost_usd / discipline.total_hours) : 0,
      utilization: (discipline.total_hours / project.total_hours) * 100,
      color: COLORS[index % COLORS.length]
    }));

    setDisciplineComparison(disciplineData);
    generateMonthlyProfitAnalysis(project, disciplineData);
  };

  const generateMonthlyProfitAnalysis = (project, disciplineData) => {
    // Calculate actual project duration from dates
    let projectMonths = project.project_duration_months || 12;
    let startDate = new Date();
    let endDate = new Date();
    
    if (project.project_start_date && project.project_end_date) {
      startDate = new Date(project.project_start_date);
      endDate = new Date(project.project_end_date);
      const diffTime = Math.abs(endDate - startDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      projectMonths = Math.max(1, Math.round(diffDays / 30.44));
    }

    // Generate monthly data based on actual project timeline
    const monthlyData = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    for (let i = 0; i < projectMonths; i++) {
      const currentDate = new Date(startDate);
      currentDate.setMonth(startDate.getMonth() + i);
      
      // More realistic distribution - front-loaded with peak in middle
      let distributionFactor;
      if (i < projectMonths * 0.3) {
        distributionFactor = 0.05 + (i / (projectMonths * 0.3)) * 0.10; // Ramp up
      } else if (i < projectMonths * 0.7) {
        distributionFactor = 0.15; // Peak activity
      } else {
        distributionFactor = 0.15 - ((i - projectMonths * 0.7) / (projectMonths * 0.3)) * 0.10; // Ramp down
      }
      
      const totalCost = project.total_cost_usd * distributionFactor;
      
      const disciplineBreakdown = disciplineData.map(discipline => ({
        name: discipline.name,
        cost: discipline.totalCost * distributionFactor,
        hours: discipline.totalHours * distributionFactor,
        profit: (discipline.totalCost * distributionFactor) * 0.15, // 15% profit margin
        breakEvenPoint: discipline.totalCost * 0.75 // 75% of total cost is break-even
      }));
      
      monthlyData.push({
        month: monthNames[currentDate.getMonth()],
        year: currentDate.getFullYear(),
        totalCost,
        totalProfit: totalCost * 0.15,
        totalRevenue: totalCost * 1.15,
        disciplines: disciplineBreakdown,
        cumulativeCost: monthlyData.reduce((sum, item) => sum + item.totalCost, 0) + totalCost,
        cumulativeRevenue: (monthlyData.reduce((sum, item) => sum + item.totalRevenue, 0) + totalCost * 1.15)
      });
    }
    
    setMonthlyProfitData(monthlyData);
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    analyzeProject(project);
  };

  const getBreakEvenAnalysis = () => {
    if (!disciplineComparison.length) return [];
    
    return disciplineComparison.map(discipline => {
      const breakEvenPoint = discipline.totalCost * 0.75; // 75% of cost is break-even
      const profitMargin = (discipline.totalCost * 1.15 - discipline.totalCost) / discipline.totalCost; // 15% target margin
      const riskLevel = discipline.totalCost > (projectDetails.total_cost_usd * 0.15) ? 'High' : 
                       discipline.totalCost > (projectDetails.total_cost_usd * 0.08) ? 'Medium' : 'Low';
      
      return {
        ...discipline,
        breakEvenPoint,
        profitMargin,
        riskLevel,
        targetRevenue: discipline.totalCost * 1.15,
        roi: ((discipline.totalCost * 1.15 - discipline.totalCost) / discipline.totalCost * 100).toFixed(1)
      };
    });
  };

  const getFilteredDisciplineData = () => {
    if (selectedDisciplineFilter === 'all') {
      return disciplineComparison;
    }
    return disciplineComparison.filter(d => d.name === selectedDisciplineFilter);
  };

  const getDisciplineInsights = (discipline) => {
    const avgEfficiency = disciplineComparison.reduce((sum, d) => sum + d.efficiency, 0) / disciplineComparison.length;
    const isAboveAverage = discipline.efficiency > avgEfficiency;
    
    return {
      performance: isAboveAverage ? 'Above Average' : 'Below Average',
      recommendation: isAboveAverage 
        ? 'Performing well - consider as benchmark'
        : 'Review resource allocation and process efficiency',
      status: isAboveAverage ? 'good' : 'warning'
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!calculations.length) {
    return (
      <Card className="text-center p-8">
        <CardContent>
          <Building className="h-12 w-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Projects Available</h3>
          <p className="text-slate-600">Calculate some projects first to see detailed analysis</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-8" id="project-analysis">
      {/* Project Selection */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center space-x-2">
                <Building className="h-5 w-5 text-indigo-600" />
                <span>Project Selection & Overview</span>
              </CardTitle>
              <CardDescription>Select a project to view detailed discipline-specific analysis</CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <span className="mr-2">🖨️</span> Print
              </Button>
              <Button variant="outline" size="sm" onClick={() => exportToPDF('project-analysis', 'project-analysis.pdf')}>
                <span className="mr-2">📄</span> Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-2">
              <Label>Select Project</Label>
              <Select 
                value={selectedProject?.id || ''} 
                onValueChange={(projectId) => {
                  const project = calculations.find(c => c.id === projectId);
                  handleProjectSelect(project);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a project to analyze" />
                </SelectTrigger>
                <SelectContent>
                  {calculations.map((calc) => (
                    <SelectItem key={calc.id} value={calc.id}>
                      <div className="flex items-center space-x-2">
                        <Building className="h-4 w-4" />
                        <div>
                          <span className="font-medium">{calc.project_name}</span>
                          <span className="text-xs text-slate-500 ml-2">
                            {new Date(calc.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {selectedProject && (
              <div className="bg-slate-50 p-6 rounded-lg">
                <h4 className="font-semibold text-slate-900 mb-4">Comprehensive Project Summary</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-slate-600">Project Name:</p>
                    <p className="font-medium">{selectedProject.project_name}</p>
                  </div>
                  <div>
                    <p className="text-slate-600">Structure Type:</p>
                    <p className="font-medium">{selectedProject.structure_type || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-slate-600">Location:</p>
                    <p className="font-medium">{selectedProject.location || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-slate-600">Complexity:</p>
                    <Badge variant={
                      selectedProject.complexity === 'low' ? 'secondary' : 
                      selectedProject.complexity === 'medium' ? 'default' : 'destructive'
                    }>
                      {selectedProject.complexity || 'Medium'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-slate-600">LOD Level:</p>
                    <p className="font-medium">{selectedProject.lod_level || 'LOD 300'}</p>
                  </div>
                  <div>
                    <p className="text-slate-600">Design Size:</p>
                    <p className="font-medium">{selectedProject.design_size?.toLocaleString() || 'N/A'} m²</p>
                  </div>
                  <div>
                    <p className="text-slate-600">Deliverables:</p>
                    <p className="font-medium">{selectedProject.num_deliverables || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-slate-600">Duration:</p>
                    <p className="font-medium">
                      {selectedProject.project_duration_months || 12} months
                    </p>
                  </div>
                </div>
                
                {selectedProject.project_start_date && selectedProject.project_end_date && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-slate-600">Start Date:</p>
                        <p className="font-medium">
                          {new Date(selectedProject.project_start_date).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-600">End Date:</p>
                        <p className="font-medium">
                          {new Date(selectedProject.project_end_date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="bg-blue-50 p-3 rounded">
                      <p className="text-slate-600">Total Budget:</p>
                      <p className="font-bold text-blue-900">${selectedProject.total_cost_usd?.toLocaleString()}</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded">
                      <p className="text-slate-600">Target Revenue:</p>
                      <p className="font-bold text-green-900">
                        ${((selectedProject.total_cost_usd || 0) * 1.15).toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-purple-50 p-3 rounded">
                      <p className="text-slate-600">Expected Profit:</p>
                      <p className="font-bold text-purple-900">
                        ${((selectedProject.total_cost_usd || 0) * 0.15).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
            {/* Discipline Filter */}
            {selectedProject && disciplineComparison.length > 0 && (
              <div className="space-y-2">
                <Label>Filter by Discipline</Label>
                <Select value={selectedDisciplineFilter} onValueChange={setSelectedDisciplineFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter disciplines" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Disciplines</SelectItem>
                    {disciplineComparison.map((discipline) => (
                      <SelectItem key={discipline.name} value={discipline.name}>
                        {discipline.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
        </CardContent>
      </Card>

      {selectedProject && projectDetails && (
        <>
          {/* Project KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Clock className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Total Hours</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {projectDetails.total_hours.toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500">{disciplineComparison.length} disciplines</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <DollarSign className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Total Cost</p>
                    <p className="text-2xl font-bold text-slate-900">
                      ${projectDetails.total_cost_usd.toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500">
                      ${Math.round(projectDetails.total_cost_usd / projectDetails.total_hours)}/hr avg
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Users className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Resource Split</p>
                    <p className="text-lg font-bold text-slate-900">
                      {Math.round(disciplineComparison.reduce((sum, d) => sum + d.engineerHours, 0) / projectDetails.total_hours * 100)}% Eng
                    </p>
                    <p className="text-xs text-slate-500">
                      {Math.round(disciplineComparison.reduce((sum, d) => sum + d.technicianHours, 0) / projectDetails.total_hours * 100)}% Tech
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Most Expensive</p>
                    <p className="text-lg font-bold text-slate-900">
                      {disciplineComparison.reduce((max, discipline) => 
                        discipline.totalCost > max.totalCost ? discipline : max
                      ).name.substring(0, 12)}
                    </p>
                    <p className="text-xs text-slate-500">discipline</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Discipline Analysis Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Hours Distribution */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Clock className="h-5 w-5" />
                  <span>Hours Distribution by Discipline</span>
                </CardTitle>
                <CardDescription>Engineer vs Technician hour allocation</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsBarChart data={getFilteredDisciplineData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45} 
                      textAnchor="end" 
                      height={100}
                      fontSize={12}
                    />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name) => [
                        `${value.toFixed(0)} hrs`,
                        name === 'engineerHours' ? 'Engineer Hours' : 'Technician Hours'
                      ]}
                    />
                    <Legend />
                    <Bar dataKey="engineerHours" stackId="a" fill="#8884d8" name="Engineer Hours" />
                    <Bar dataKey="technicianHours" stackId="a" fill="#82ca9d" name="Technician Hours" />
                  </RechartsBarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Cost Distribution */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <DollarSign className="h-5 w-5" />
                  <span>Cost Breakdown by Discipline</span>
                </CardTitle>
                <CardDescription>Total cost allocation across disciplines</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPieChart>
                    <Pie
                      data={getFilteredDisciplineData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name.substring(0, 8)} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="totalCost"
                    >
                      {getFilteredDisciplineData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Cost']} />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Efficiency Analysis */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <BarChart3 className="h-5 w-5" />
                  <span>Discipline Efficiency Comparison</span>
                </CardTitle>
                <CardDescription>Cost per hour by discipline</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsBarChart data={getFilteredDisciplineData().sort((a, b) => b.efficiency - a.efficiency)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45} 
                      textAnchor="end" 
                      height={100}
                      fontSize={12}
                    />
                    <YAxis tickFormatter={(value) => `$${value}`} />
                    <Tooltip 
                      formatter={(value) => [`$${value.toFixed(2)}/hr`, 'Cost per Hour']}
                      labelFormatter={(label) => `Discipline: ${label}`}
                    />
                    <Bar 
                      dataKey="efficiency" 
                      fill="#ffc658"
                      radius={[4, 4, 0, 0]}
                    />
                  </RechartsBarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Utilization Analysis */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <PieChart className="h-5 w-5" />
                  <span>Project Utilization Rates</span>
                </CardTitle>
                <CardDescription>Percentage of total project hours by discipline</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsBarChart data={getFilteredDisciplineData().sort((a, b) => b.utilization - a.utilization)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45} 
                      textAnchor="end" 
                      height={100}
                      fontSize={12}
                    />
                    <YAxis tickFormatter={(value) => `${value}%`} />
                    <Tooltip 
                      formatter={(value) => [`${value.toFixed(1)}%`, 'Utilization']}
                      labelFormatter={(label) => `Discipline: ${label}`}
                    />
                    <Bar 
                      dataKey="utilization" 
                      fill="#8dd1e1"
                      radius={[4, 4, 0, 0]}
                    />
                  </RechartsBarChart>
                </ResponsiveContainer>
              </CardContent>
          </Card>
          </div>

          {/* Discipline Analysis Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Hours Distribution */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Clock className="h-5 w-5" />
                  <span>Hours Distribution by Discipline</span>
                </CardTitle>
                <CardDescription>Engineer vs Technician hour allocation</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsBarChart data={getFilteredDisciplineData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45} 
                      textAnchor="end" 
                      height={100}
                      fontSize={12}
                    />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name) => [
                        `${value.toFixed(0)} hrs`,
                        name === 'engineerHours' ? 'Engineer Hours' : 'Technician Hours'
                      ]}
                    />
                    <Legend />
                    <Bar dataKey="engineerHours" stackId="a" fill="#8884d8" name="Engineer Hours" />
                    <Bar dataKey="technicianHours" stackId="a" fill="#82ca9d" name="Technician Hours" />
                  </RechartsBarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Cost Distribution */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <DollarSign className="h-5 w-5" />
                  <span>Cost Breakdown by Discipline</span>
                </CardTitle>
                <CardDescription>Total cost allocation across disciplines</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPieChart>
                    <Pie
                      data={getFilteredDisciplineData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name.substring(0, 8)} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="totalCost"
                    >
                      {getFilteredDisciplineData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Cost']} />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Efficiency Analysis */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <BarChart3 className="h-5 w-5" />
                  <span>Discipline Efficiency Comparison</span>
                </CardTitle>
                <CardDescription>Cost per hour by discipline</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsBarChart data={getFilteredDisciplineData().sort((a, b) => b.efficiency - a.efficiency)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45} 
                      textAnchor="end" 
                      height={100}
                      fontSize={12}
                    />
                    <YAxis tickFormatter={(value) => `$${value}`} />
                    <Tooltip 
                      formatter={(value) => [`$${value.toFixed(2)}/hr`, 'Cost per Hour']}
                      labelFormatter={(label) => `Discipline: ${label}`}
                    />
                    <Bar 
                      dataKey="efficiency" 
                      fill="#ffc658"
                      radius={[4, 4, 0, 0]}
                    />
                  </RechartsBarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Utilization Analysis */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <PieChart className="h-5 w-5" />
                  <span>Project Utilization Rates</span>
                </CardTitle>
                <CardDescription>Percentage of total project hours by discipline</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsBarChart data={getFilteredDisciplineData().sort((a, b) => b.utilization - a.utilization)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45} 
                      textAnchor="end" 
                      height={100}
                      fontSize={12}
                    />
                    <YAxis tickFormatter={(value) => `${value}%`} />
                    <Tooltip 
                      formatter={(value) => [`${value.toFixed(1)}%`, 'Utilization']}
                      labelFormatter={(label) => `Discipline: ${label}`}
                    />
                    <Bar 
                      dataKey="utilization" 
                      fill="#8dd1e1"
                      radius={[4, 4, 0, 0]}
                    />
                  </RechartsBarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Monthly Profit Analysis */}
            <Card className="hover:shadow-lg transition-shadow col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <TrendingUp className="h-5 w-5" />
                  <span>Project Timeline Analysis</span>
                </CardTitle>
                <CardDescription>
                  {selectedProject && selectedProject.project_start_date && selectedProject.project_end_date 
                    ? `Timeline: ${new Date(selectedProject.project_start_date).toLocaleDateString()} - ${new Date(selectedProject.project_end_date).toLocaleDateString()}`
                    : 'Month-on-month cost and revenue progression'
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={monthlyProfitData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="month" 
                      tickFormatter={(value, index) => {
                        const data = monthlyProfitData[index];
                        return data ? `${value} ${data.year}` : value;
                      }}
                    />
                    <YAxis tickFormatter={(value) => `$${(value/1000).toFixed(0)}K`} />
                    <Tooltip 
                      formatter={(value, name) => [
                        `$${value.toLocaleString()}`,
                        name === 'totalCost' ? 'Monthly Cost' : 
                        name === 'totalRevenue' ? 'Monthly Revenue' :
                        name === 'cumulativeCost' ? 'Cumulative Cost' : 'Cumulative Revenue'
                      ]}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="totalCost" 
                      stroke="#8884d8" 
                      name="Monthly Cost" 
                      strokeWidth={2}
                      dot={{ fill: '#8884d8', strokeWidth: 2, r: 4 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="totalRevenue" 
                      stroke="#82ca9d" 
                      name="Monthly Revenue" 
                      strokeWidth={2}
                      dot={{ fill: '#82ca9d', strokeWidth: 2, r: 4 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="cumulativeCost" 
                      stroke="#ffc658" 
                      name="Cumulative Cost" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ fill: '#ffc658', strokeWidth: 2, r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Break Even Analysis */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <DollarSign className="h-5 w-5" />
                <span>Discipline-wise Break-Even Analysis</span>
              </CardTitle>
              <CardDescription>
                Financial benchmarks and risk assessment for each discipline
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
                <table className="w-full border-collapse min-w-[600px]">
                  <thead>
                    <tr className="bg-slate-50 border-b">
                      <th className="text-left p-4 font-semibold">Discipline</th>
                      <th className="text-right p-4 font-semibold">Total Cost</th>
                      <th className="text-right p-4 font-semibold">Break-Even Point</th>
                      <th className="text-right p-4 font-semibold">Target Revenue</th>
                      <th className="text-right p-4 font-semibold">Expected Profit</th>
                      <th className="text-right p-4 font-semibold">ROI %</th>
                      <th className="text-center p-4 font-semibold">Risk Level</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getBreakEvenAnalysis().map((discipline, index) => (
                      <tr key={index} className="border-b hover:bg-slate-50/50 transition-colors">
                        <td className="p-4">
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: discipline.color }}
                            />
                            <span className="font-medium">{discipline.name}</span>
                          </div>
                        </td>
                        <td className="text-right p-4 font-semibold">
                          ${discipline.totalCost.toLocaleString()}
                        </td>
                        <td className="text-right p-4 font-semibold text-orange-600">
                          ${discipline.breakEvenPoint.toLocaleString()}
                        </td>
                        <td className="text-right p-4 font-semibold text-green-600">
                          ${discipline.targetRevenue.toLocaleString()}
                        </td>
                        <td className="text-right p-4 font-semibold text-blue-600">
                          ${(discipline.targetRevenue - discipline.totalCost).toLocaleString()}
                        </td>
                        <td className="text-right p-4 font-bold">
                          {discipline.roi}%
                        </td>
                        <td className="text-center p-4">
                          <Badge variant={
                            discipline.riskLevel === 'Low' ? 'secondary' : 
                            discipline.riskLevel === 'Medium' ? 'default' : 'destructive'
                          }>
                            {discipline.riskLevel}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Summary Cards */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2">Total Project</h4>
                  <p className="text-2xl font-bold text-blue-900">
                    ${projectDetails.total_cost_usd.toLocaleString()}
                  </p>
                  <p className="text-sm text-blue-600">Total Investment</p>
                </div>
                
                <div className="bg-orange-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-orange-900 mb-2">Break-Even</h4>
                  <p className="text-2xl font-bold text-orange-900">
                    ${(projectDetails.total_cost_usd * 0.75).toLocaleString()}
                  </p>
                  <p className="text-sm text-orange-600">75% of Total Cost</p>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-900 mb-2">Target Revenue</h4>
                  <p className="text-2xl font-bold text-green-900">
                    ${(projectDetails.total_cost_usd * 1.15).toLocaleString()}
                  </p>
                  <p className="text-sm text-green-600">15% Profit Margin</p>
                </div>
                
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-purple-900 mb-2">Expected Profit</h4>
                  <p className="text-2xl font-bold text-purple-900">
                    ${(projectDetails.total_cost_usd * 0.15).toLocaleString()}
                  </p>
                  <p className="text-sm text-purple-600">15% ROI Target</p>
                </div>
              </div>
            </CardContent>
          </Card>

        {/* Detailed Discipline Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Settings className="h-5 w-5" />
              <span>Detailed Discipline Analysis</span>
            </CardTitle>
            <CardDescription>
              Comprehensive breakdown with insights and recommendations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
              <table className="w-full border-collapse min-w-[600px]">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="text-left p-4 font-semibold">Discipline</th>
                    <th className="text-right p-4 font-semibold">Total Hours</th>
                    <th className="text-right p-4 font-semibold">Total Cost</th>
                    <th className="text-right p-4 font-semibold">Efficiency</th>
                    <th className="text-right p-4 font-semibold">Utilization</th>
                    <th className="text-center p-4 font-semibold">Performance</th>
                    <th className="text-center p-4 font-semibold">Insights</th>
                  </tr>
                </thead>
                <tbody>
                  {disciplineComparison.map((discipline, index) => {
                    const insights = getDisciplineInsights(discipline);
                    return (
                      <tr key={index} className="border-b hover:bg-slate-50/50 transition-colors">
                        <td className="p-4">
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: discipline.color }}
                            />
                            <span className="font-medium">{discipline.name}</span>
                          </div>
                        </td>
                        <td className="text-right p-4">
                          <div>
                            <p className="font-semibold">{discipline.totalHours.toFixed(0)}</p>
                            <p className="text-xs text-slate-500">
                              {discipline.engineerHours.toFixed(0)} eng / {discipline.technicianHours.toFixed(0)} tech
                            </p>
                          </div>
                        </td>
                        <td className="text-right p-4">
                          <div>
                            <p className="font-semibold">${discipline.totalCost.toLocaleString()}</p>
                            <p className="text-xs text-slate-500">
                              ${discipline.engineerCost.toLocaleString()} eng
                            </p>
                          </div>
                        </td>
                        <td className="text-right p-4">
                          <p className="font-semibold">${discipline.efficiency.toFixed(2)}/hr</p>
                        </td>
                        <td className="text-right p-4">
                          <p className="font-semibold">{discipline.utilization.toFixed(1)}%</p>
                        </td>
                        <td className="text-center p-4">
                          <Badge variant={insights.status === 'good' ? 'default' : 'secondary'}>
                            {insights.performance}
                          </Badge>
                        </td>
                        <td className="text-center p-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toast.info(`${discipline.name}: ${insights.recommendation}`)}
                          >
                            <Info className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Key Insights Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5" />
              <span>Project Insights & Recommendations</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">Most Efficient Discipline</h4>
                <p className="text-sm text-blue-700">
                  {disciplineComparison.reduce((min, discipline) => 
                    discipline.efficiency < min.efficiency ? discipline : min
                  ).name}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  ${disciplineComparison.reduce((min, discipline) => 
                    discipline.efficiency < min.efficiency ? discipline : min
                  ).efficiency.toFixed(2)}/hr
                </p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-green-900 mb-2">Largest Resource Consumer</h4>
                <p className="text-sm text-green-700">
                  {disciplineComparison.reduce((max, discipline) => 
                    discipline.utilization > max.utilization ? discipline : max
                  ).name}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  {disciplineComparison.reduce((max, discipline) => 
                    discipline.utilization > max.utilization ? discipline : max
                  ).utilization.toFixed(1)}% of project
                </p>
              </div>
              
              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="font-semibold text-orange-900 mb-2">Cost Distribution</h4>
                <p className="text-sm text-orange-700">
                  {Math.round(disciplineComparison.reduce((sum, d) => sum + d.engineerCost, 0) / projectDetails.total_cost_usd * 100)}% Engineer Costs
                </p>
                <p className="text-xs text-orange-600 mt-1">
                  {Math.round(disciplineComparison.reduce((sum, d) => sum + d.technicianCost, 0) / projectDetails.total_cost_usd * 100)}% Technician Costs
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </>
    )}
  </div>
);
};

const RatesManagement = () => {
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regions, setRegions] = useState([]);
  const [seniorityLevels, setSeniorityLevels] = useState([]);
  const [selectedOptimizationScenario, setSelectedOptimizationScenario] = useState('cost-minimize');
  const [optimizationData, setOptimizationData] = useState(null);
  const [loadingOptimization, setLoadingOptimization] = useState(false);
  const [showOptimization, setShowOptimization] = useState(false);

  useEffect(() => {
    fetchRates();
  }, []);

  const fetchRates = async () => {
    try {
      const response = await axios.get(`${API}/rates`);
      setRates(response.data.rates);
      setRegions(response.data.regions);
      setSeniorityLevels(response.data.seniority_levels);
    } catch (error) {
      console.error('Error fetching rates:', error);
      toast.error('Failed to load rates');
    } finally {
      setLoading(false);
    }
  };

  const handleRateChange = (region, seniorityLevel, value) => {
    setRates(prev => ({
      ...prev,
      [region]: {
        ...prev[region],
        [seniorityLevel]: parseFloat(value) || 0
      }
    }));
  };

  const saveRates = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/rates/update`, rates);
      toast.success('Rates updated successfully!');
    } catch (error) {
      console.error('Error saving rates:', error);
      toast.error('Failed to save rates');
    } finally {
      setSaving(false);
    }
  };

  const fetchOptimizationData = async () => {
    setLoadingOptimization(true);
    try {
      const response = await axios.post(`${API}/optimize-resources`, {});
      setOptimizationData(response.data);
      setShowOptimization(true);
      toast.success('Optimization analysis complete!');
    } catch (error) {
      console.error('Error fetching optimization data:', error);
      toast.error('Failed to load optimization data');
    } finally {
      setLoadingOptimization(false);
    }
  };

  const getRegionalComparison = () => {
    if (!rates || Object.keys(rates).length === 0) return [];
    
    const comparison = seniorityLevels.map(level => {
      const regional_rates = {};
      let minRate = Infinity;
      let maxRate = 0;
      let minRegion = '';
      let maxRegion = '';
      
      regions.forEach(region => {
        const rate = rates[region]?.[level] || 0;
        regional_rates[region] = rate;
        
        if (rate < minRate && rate > 0) {
          minRate = rate;
          minRegion = region;
        }
        if (rate > maxRate) {
          maxRate = rate;
          maxRegion = region;
        }
      });
      
      const avgRate = Object.values(regional_rates).reduce((sum, rate) => sum + rate, 0) / regions.length;
      const costSaving = maxRate - minRate;
      const savingPercentage = maxRate > 0 ? ((costSaving / maxRate) * 100).toFixed(1) : 0;
      
      return {
        seniorityLevel: level,
        rates: regional_rates,
        minRate,
        maxRate,
        minRegion,
        maxRegion,
        avgRate,
        costSaving,
        savingPercentage
      };
    });
    
    return comparison;
  };

  const getOptimalResourceMix = () => {
    if (!rates || Object.keys(rates).length === 0) return {};
    
    const engineerLevels = seniorityLevels.filter(level => level.includes('engineer'));
    const technicianLevels = seniorityLevels.filter(level => level.includes('technician'));
    const managerLevels = seniorityLevels.filter(level => 
      level.includes('manager') || level.includes('director') || level.includes('architect')
    );
    
    const findOptimalRegion = (levels, criteria = 'cost') => {
      const regionCosts = {};
      
      regions.forEach(region => {
        let totalCost = 0;
        levels.forEach(level => {
          totalCost += rates[region]?.[level] || 0;
        });
        regionCosts[region] = totalCost / levels.length; // Average cost for this category
      });
      
      if (criteria === 'cost') {
        // Find lowest cost region
        return Object.entries(regionCosts).reduce((min, [region, cost]) => 
          cost < min.cost ? { region, cost } : min
        , { region: '', cost: Infinity });
      } else {
        // Find highest value region (for senior positions)
        return Object.entries(regionCosts).reduce((max, [region, cost]) => 
          cost > max.cost ? { region, cost } : max
        , { region: '', cost: 0 });
      }
    };
    
    const optimalEngineerRegion = findOptimalRegion(engineerLevels, 'cost');
    const optimalTechnicianRegion = findOptimalRegion(technicianLevels, 'cost');
    const optimalManagerRegion = findOptimalRegion(managerLevels, 'value');
    
    // Calculate cost savings
    const avgEngineerCost = regions.reduce((sum, region) => {
      return sum + engineerLevels.reduce((levelSum, level) => 
        levelSum + (rates[region]?.[level] || 0), 0) / engineerLevels.length;
    }, 0) / regions.length;
    
    const avgTechnicianCost = regions.reduce((sum, region) => {
      return sum + technicianLevels.reduce((levelSum, level) => 
        levelSum + (rates[region]?.[level] || 0), 0) / technicianLevels.length;
    }, 0) / regions.length;
    
    const engineerSavings = avgEngineerCost - optimalEngineerRegion.cost;
    const technicianSavings = avgTechnicianCost - optimalTechnicianRegion.cost;
    
    return {
      engineers: optimalEngineerRegion,
      technicians: optimalTechnicianRegion,
      managers: optimalManagerRegion,
      totalSavings: engineerSavings + technicianSavings,
      engineerSavings,
      technicianSavings
    };
  };

  const getOptimizationScenarios = () => {
    if (!rates || Object.keys(rates).length === 0) return [];
    
    const scenarios = [
      {
        id: 'cost-minimize',
        name: 'Cost Minimization',
        description: 'Minimize overall project costs by selecting lowest-cost regions for each role',
        strategy: 'Use Asia for technical roles, Europe for management',
        estimatedSavings: '25-35%'
      },
      {
        id: 'quality-balance',
        name: 'Quality-Cost Balance',
        description: 'Balance cost efficiency with skill quality and timezone considerations',
        strategy: 'Mix of Europe/UK for senior roles, Asia for junior positions',
        estimatedSavings: '15-25%'
      },
      {
        id: 'proximity-focus',
        name: 'Proximity Optimization',
        description: 'Optimize for communication and collaboration while maintaining cost efficiency',
        strategy: 'Regional clustering with cost-effective satellite teams',
        estimatedSavings: '10-20%'
      },
      {
        id: 'hybrid-model',
        name: 'Hybrid Excellence',
        description: 'Strategic mix of onshore/offshore resources for maximum value',
        strategy: 'UK leadership, Europe/USA expertise, Asia volume delivery',
        estimatedSavings: '20-30%'
      }
    ];
    
    return scenarios;
  };

  const regionalComparison = getRegionalComparison();
  const optimalMix = getOptimalResourceMix();
  const scenarios = getOptimizationScenarios();

  const getSeniorityLabel = (level) => {
    const labels = {
      'junior_engineer': 'Junior Engineer',
      'senior_engineer': 'Senior Engineer', 
      'principal_engineer': 'Principal Engineer',
      'junior_technician': 'Junior Technician',
      'senior_technician': 'Senior Technician',
      'engineering_manager': 'Engineering Manager',
      'cad_bim_manager': 'CAD/BIM Manager',
      'project_manager': 'Project Manager',
      'design_manager': 'Design Manager',
      'project_director': 'Project Director',
      'discipline_director': 'Discipline Director',
      'architect': 'Architect',
      'senior_architect': 'Senior Architect',
      'lead_architect': 'Lead Architect',
      'project_control_manager': 'Project Control Manager',
      'project_control_engineer': 'Project Control Engineer',
      'planner': 'Planner',
      'senior_planner': 'Senior Planner',
      'lead_commercial_manager': 'Lead Commercial Manager',
      'quality_manager': 'Quality Manager',
      'environmental_engineer': 'Environmental Engineer',
      'structural_engineer': 'Structural Engineer',
      'mep_engineer': 'MEP Engineer',
      'civil_engineer': 'Civil Engineer',
      'geotechnical_engineer': 'Geotechnical Engineer',
      'fire_engineer': 'Fire Engineer',
      'acoustic_engineer': 'Acoustic Engineer',
      'facade_engineer': 'Facade Engineer',
      'sustainability_engineer': 'Sustainability Engineer',
      'lighting_engineer': 'Lighting Engineer',
      'security_engineer': 'Security Engineer',
      'telecom_engineer': 'Telecom Engineer'
    };
    return labels[level] || level.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" id="rates-management">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center space-x-2">
                <Users className="h-5 w-5" />
                <span>User-Defined Hourly Rates</span>
              </CardTitle>
              <CardDescription>
                Customize hourly rates for engineers and technicians by region and seniority level
              </CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <span className="mr-2">🖨️</span> Print
              </Button>
              <Button variant="outline" size="sm" onClick={() => exportToPDF('rates-management', 'rates.pdf')}>
                <span className="mr-2">📄</span> Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {regions.map(region => (
            <div key={region} className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">
                {region}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {seniorityLevels.map(level => (
                  <div key={level} className="space-y-2">
                    <Label htmlFor={`${region}-${level}`} className="text-xs font-medium">
                      {getSeniorityLabel(level)}
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 text-sm">$</span>
                      <Input
                        id={`${region}-${level}`}
                        type="number"
                        min="0"
                        step="0.01"
                        className="pl-8 text-sm"
                        value={rates[region]?.[level] || 0}
                        onChange={(e) => handleRateChange(region, level, e.target.value)}
                        data-testid={`rate-${region.toLowerCase()}-${level}`}
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 text-xs">/hr</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          
          <div className="flex justify-end pt-6 border-t">
            <Button
              onClick={saveRates}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-8"
              data-testid="save-rates-button"
            >
              {saving ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </div>
              ) : (
                'Save Rates'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Cost Optimization Section */}
      <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="h-6 w-6 text-indigo-600" />
            <span>Regional Cost Optimization</span>
          </CardTitle>
          <CardDescription>
            Analyze regional costs and discover optimal resource allocation strategies to minimize costs and maximize profit
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">
              Get AI-powered recommendations for resource allocation across regions
            </p>
            <Button
              onClick={fetchOptimizationData}
              disabled={loadingOptimization}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {loadingOptimization ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Analyzing...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <BarChart3 className="h-4 w-4" />
                  <span>Run Optimization Analysis</span>
                </div>
              )}
            </Button>
          </div>

          {showOptimization && optimizationData && (
            <div className="space-y-6 mt-6">
              {/* Optimization Scenarios */}
              <div>
                <h3 className="text-lg font-semibold mb-4 text-slate-900">Optimization Scenarios</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                  {optimizationData.scenarios.map((scenario) => (
                    <Card 
                      key={scenario.id} 
                      className={`cursor-pointer transition-all hover:shadow-lg ${
                        selectedOptimizationScenario === scenario.id 
                          ? 'border-2 border-indigo-500 bg-indigo-50' 
                          : 'border hover:border-indigo-300'
                      }`}
                      onClick={() => setSelectedOptimizationScenario(scenario.id)}
                    >
                      <CardContent className="p-4">
                        <h4 className="font-semibold text-base mb-2">{scenario.name}</h4>
                        <p className="text-xs text-slate-600 mb-3">{scenario.description}</p>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-slate-500">Savings:</span>
                            <Badge variant="success" className="bg-green-100 text-green-800">
                              {scenario.estimated_savings_percentage.toFixed(1)}%
                            </Badge>
                          </div>
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-slate-500">Cost Reduction:</span>
                            <span className="font-semibold text-green-600">
                              ${scenario.estimated_savings_usd.toFixed(2)}/hr
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Selected Scenario Details */}
              {selectedOptimizationScenario && (
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="text-lg">
                      {optimizationData.scenarios.find(s => s.id === selectedOptimizationScenario)?.name} - Recommended Resource Allocation
                    </CardTitle>
                    <CardDescription>
                      Optimal regional distribution for each resource category
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                      {Object.entries(optimizationData.scenarios.find(s => s.id === selectedOptimizationScenario)?.recommendation || {}).map(([category, region]) => (
                        <div key={category} className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                          <div className="text-xs text-slate-500 mb-1 uppercase font-medium">{category}</div>
                          <div className="text-lg font-bold text-indigo-600">{region}</div>
                          <div className="text-xs text-slate-600 mt-2">
                            Avg: ${optimizationData.regional_analysis[region][`${category.slice(0, -1)}_avg`]?.toFixed(2)}/hr
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <div className="text-sm text-blue-600 font-medium">Baseline Cost</div>
                        <div className="text-2xl font-bold text-blue-900">
                          ${optimizationData.scenarios.find(s => s.id === selectedOptimizationScenario)?.baseline_cost.toFixed(2)}
                        </div>
                        <div className="text-xs text-blue-600">per hour (all categories)</div>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <div className="text-sm text-green-600 font-medium">Optimized Cost</div>
                        <div className="text-2xl font-bold text-green-900">
                          ${optimizationData.scenarios.find(s => s.id === selectedOptimizationScenario)?.optimized_cost.toFixed(2)}
                        </div>
                        <div className="text-xs text-green-600">per hour (all categories)</div>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                        <div className="text-sm text-purple-600 font-medium">Total Savings</div>
                        <div className="text-2xl font-bold text-purple-900">
                          {optimizationData.scenarios.find(s => s.id === selectedOptimizationScenario)?.estimated_savings_percentage.toFixed(1)}%
                        </div>
                        <div className="text-xs text-purple-600">
                          ${optimizationData.scenarios.find(s => s.id === selectedOptimizationScenario)?.estimated_savings_usd.toFixed(2)}/hr
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Regional Cost Comparison Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Regional Cost Analysis by Category</CardTitle>
                  <CardDescription>Average hourly rates across regions for different resource types</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <RechartsBarChart 
                      data={Object.entries(optimizationData.regional_analysis).map(([region, data]) => ({
                        region,
                        Engineers: data.engineer_avg,
                        Technicians: data.technician_avg,
                        Managers: data.manager_avg,
                        Architects: data.architect_avg,
                        Specialists: data.specialist_avg
                      }))}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="region" />
                      <YAxis tickFormatter={(value) => `$${value}`} />
                      <Tooltip formatter={(value) => `$${value.toFixed(2)}/hr`} />
                      <Legend />
                      <Bar dataKey="Engineers" fill="#8884d8" />
                      <Bar dataKey="Technicians" fill="#82ca9d" />
                      <Bar dataKey="Managers" fill="#ffc658" />
                      <Bar dataKey="Architects" fill="#ff7300" />
                      <Bar dataKey="Specialists" fill="#8dd1e1" />
                    </RechartsBarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Detailed Comparison Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Detailed Rate Comparison & Savings Potential</CardTitle>
                  <CardDescription>Compare rates across regions and identify cost-saving opportunities for each role</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
                    <table className="w-full border-collapse text-sm min-w-[600px]">
                      <thead>
                        <tr className="bg-slate-50 border-b">
                          <th className="text-left p-3 font-semibold">Role</th>
                          <th className="text-center p-3 font-semibold">Best Region</th>
                          <th className="text-center p-3 font-semibold">Min Rate</th>
                          <th className="text-center p-3 font-semibold">Worst Region</th>
                          <th className="text-center p-3 font-semibold">Max Rate</th>
                          <th className="text-center p-3 font-semibold">Savings</th>
                        </tr>
                      </thead>
                      <tbody>
                        {optimizationData.comparison_table
                          .filter(item => item.cost_savings > 0)
                          .sort((a, b) => b.cost_savings - a.cost_savings)
                          .slice(0, 15)
                          .map((item) => (
                          <tr key={item.seniority_level} className="border-b hover:bg-slate-50/50">
                            <td className="p-3 font-medium">{getSeniorityLabel(item.seniority_level)}</td>
                            <td className="text-center p-3">
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                                {item.min_region}
                              </Badge>
                            </td>
                            <td className="text-center p-3 text-green-600 font-semibold">
                              ${item.min_rate.toFixed(2)}/hr
                            </td>
                            <td className="text-center p-3">
                              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
                                {item.max_region}
                              </Badge>
                            </td>
                            <td className="text-center p-3 text-red-600 font-semibold">
                              ${item.max_rate.toFixed(2)}/hr
                            </td>
                            <td className="text-center p-3">
                              <div className="flex flex-col items-center">
                                <span className="font-bold text-purple-600">
                                  ${item.cost_savings.toFixed(2)}/hr
                                </span>
                                <Badge className="bg-purple-100 text-purple-800 text-xs mt-1">
                                  {item.savings_percentage.toFixed(0)}%
                                </Badge>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rate Comparison Table */}
      <Card>
        <CardHeader>
          <CardTitle>Rate Comparison by Region</CardTitle>
          <CardDescription>
            Compare hourly rates across different regions and seniority levels
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
            <table className="w-full border-collapse min-w-[600px]">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="text-left p-4 font-semibold">Seniority Level</th>
                  {regions.map(region => (
                    <th key={region} className="text-center p-4 font-semibold">{region}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {seniorityLevels.map(level => (
                  <tr key={level} className="border-b hover:bg-slate-50/50">
                    <td className="p-4 font-medium">{getSeniorityLabel(level)}</td>
                    {regions.map(region => (
                      <td key={region} className="text-center p-4">
                        ${(rates[region]?.[level] || 0).toFixed(2)}/hr
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const ResourceOptimization = () => {
  const [optimizationData, setOptimizationData] = useState(null);
  const [loadingOptimization, setLoadingOptimization] = useState(false);
  const [selectedOptimizationScenario, setSelectedOptimizationScenario] = useState('cost-minimize');
  const [expandedSections, setExpandedSections] = useState({
    scenarios: true,
    comparison: true,
    details: false
  });

  const fetchOptimizationData = async () => {
    setLoadingOptimization(true);
    try {
      // Get preferred region from current project
      const savedProject = localStorage.getItem('currentProject');
      const preferredRegion = savedProject ? JSON.parse(savedProject).location : null;
      
      const response = await axios.post(`${API}/optimize-resources`, {
        preferred_region: preferredRegion
      });
      setOptimizationData(response.data);
      toast.success('Optimization analysis complete!');
    } catch (error) {
      console.error('Error fetching optimization data:', error);
      toast.error('Failed to load optimization data');
    } finally {
      setLoadingOptimization(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getSeniorityLabel = (level) => {
    const labels = {
      'junior_engineer': 'Junior Engineer',
      'senior_engineer': 'Senior Engineer',
      'principal_engineer': 'Principal Engineer',
      'junior_technician': 'Junior Technician',
      'senior_technician': 'Senior Technician',
      'engineering_manager': 'Engineering Manager',
      'cad_bim_manager': 'CAD/BIM Manager',
      'project_manager': 'Project Manager',
      'design_manager': 'Design Manager',
      'project_director': 'Project Director',
      'discipline_director': 'Discipline Director',
      'architect': 'Architect',
      'senior_architect': 'Senior Architect',
      'lead_architect': 'Lead Architect',
      'project_control_manager': 'Project Control Manager',
      'project_control_engineer': 'Project Control Engineer',
      'planner': 'Planner',
      'senior_planner': 'Senior Planner',
      'lead_commercial_manager': 'Lead Commercial Manager',
      'quality_manager': 'Quality Manager',
      'environmental_engineer': 'Environmental Engineer',
      'structural_engineer': 'Structural Engineer',
      'mep_engineer': 'MEP Engineer',
      'civil_engineer': 'Civil Engineer',
      'geotechnical_engineer': 'Geotechnical Engineer',
      'fire_engineer': 'Fire Engineer',
      'acoustic_engineer': 'Acoustic Engineer',
      'facade_engineer': 'Facade Engineer',
      'sustainability_engineer': 'Sustainability Engineer',
      'lighting_engineer': 'Lighting Engineer',
      'security_engineer': 'Security Engineer',
      'telecom_engineer': 'Telecom Engineer'
    };
    return labels[level] || level.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="space-y-6" id="resource-optimization">
      <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center space-x-2">
                <TrendingUp className="h-6 w-6 text-indigo-600" />
                <span>Regional Cost Optimization</span>
              </CardTitle>
              <CardDescription>
                Analyze regional costs and discover optimal resource allocation strategies
              </CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <span className="mr-2">🖨️</span> Print
              </Button>
              <Button variant="outline" size="sm" onClick={() => exportToPDF('resource-optimization', 'resource-optimization.pdf')}>
                <span className="mr-2">📄</span> Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Button
            onClick={fetchOptimizationData}
            disabled={loadingOptimization}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {loadingOptimization ? 'Analyzing...' : 'Run Optimization Analysis'}
          </Button>
        </CardContent>
      </Card>

      {optimizationData && (
        <>
          <Card>
            <CardHeader className="cursor-pointer hover:bg-slate-50" onClick={() => toggleSection('scenarios')}>
              <div className="flex items-center justify-between">
                <CardTitle>💡 Optimization Scenarios</CardTitle>
                <Badge>{expandedSections.scenarios ? '▼' : '▶'}</Badge>
              </div>
            </CardHeader>
            {expandedSections.scenarios && (
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                  {optimizationData.scenarios.map((scenario) => (
                    <Card key={scenario.id} className={`cursor-pointer ${selectedOptimizationScenario === scenario.id ? 'border-2 border-indigo-500' : ''}`} onClick={() => setSelectedOptimizationScenario(scenario.id)}>
                      <CardContent className="p-4">
                        <h4 className="font-semibold mb-2">{scenario.name}</h4>
                        <p className="text-xs text-slate-600 mb-3">{scenario.description}</p>
                        <Badge className="bg-green-100 text-green-800">{scenario.estimated_savings_percentage.toFixed(1)}%</Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                {selectedOptimizationScenario && (
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-5 gap-4">
                    {Object.entries(optimizationData.scenarios.find(s => s.id === selectedOptimizationScenario)?.recommendation || {}).map(([category, region]) => (
                      <div key={category} className="bg-slate-50 p-4 rounded-lg">
                        <div className="text-xs text-slate-500 uppercase">{category}</div>
                        <div className="text-lg font-bold text-indigo-600">{region}</div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          <Card>
            <CardHeader className="cursor-pointer hover:bg-slate-50" onClick={() => toggleSection('comparison')}>
              <div className="flex items-center justify-between">
                <CardTitle>📊 Regional Cost Comparison</CardTitle>
                <Badge>{expandedSections.comparison ? '▼' : '▶'}</Badge>
              </div>
            </CardHeader>
            {expandedSections.comparison && (
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <RechartsBarChart data={Object.entries(optimizationData.regional_analysis).map(([region, data]) => ({ region, Engineers: data.engineer_avg, Technicians: data.technician_avg, Managers: data.manager_avg }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="region" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Engineers" fill="#8884d8" />
                    <Bar dataKey="Technicians" fill="#82ca9d" />
                    <Bar dataKey="Managers" fill="#ffc658" />
                  </RechartsBarChart>
                </ResponsiveContainer>
              </CardContent>
            )}
          </Card>

          <Card className="border-2 border-indigo-200">
            <CardHeader className="cursor-pointer hover:bg-slate-50" onClick={() => toggleSection('details')}>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <DollarSign className="h-5 w-5 text-indigo-600" />
                    <span>Detailed Rate Comparison & Savings Potential</span>
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Compare rates across regions and identify cost-saving opportunities for each role
                  </CardDescription>
                </div>
                <Badge className="text-lg">{expandedSections.details ? '▼' : '▶'}</Badge>
              </div>
            </CardHeader>
            {expandedSections.details && (
              <CardContent>
                <div className="mb-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
                  <h4 className="font-semibold text-blue-900 mb-2">💡 How to Read This Table</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• <strong>Best Region:</strong> Lowest cost option for each role (green highlight)</li>
                    <li>• <strong>Worst Region:</strong> Highest cost option for each role (red highlight)</li>
                    <li>• <strong>Savings:</strong> Potential hourly savings by choosing best region over worst</li>
                    <li>• <strong>Savings %:</strong> Percentage savings opportunity for cost optimization</li>
                  </ul>
                </div>

                <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
                  <table className="w-full border-collapse text-sm min-w-[600px]">
                    <thead>
                      <tr className="bg-gradient-to-r from-slate-100 to-indigo-50 border-b-2 border-indigo-200">
                        <th className="text-left p-4 font-semibold text-slate-700">Role</th>
                        <th className="text-center p-4 font-semibold text-slate-700">Best Region</th>
                        <th className="text-center p-4 font-semibold text-slate-700">Min Rate</th>
                        <th className="text-center p-4 font-semibold text-slate-700">Worst Region</th>
                        <th className="text-center p-4 font-semibold text-slate-700">Max Rate</th>
                        <th className="text-center p-4 font-semibold text-slate-700">Potential Savings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {optimizationData.comparison_table
                        .filter(item => item.cost_savings > 0)
                        .sort((a, b) => b.cost_savings - a.cost_savings)
                        .map((item, index) => (
                        <tr 
                          key={item.seniority_level} 
                          className={`border-b hover:bg-indigo-50/30 transition-colors ${
                            index < 5 ? 'bg-yellow-50/20' : ''
                          }`}
                        >
                          <td className="p-4 font-medium text-slate-900">
                            <div className="flex items-center space-x-2">
                              {index < 5 && (
                                <Badge variant="outline" className="bg-yellow-100 text-yellow-800 text-xs">
                                  Top {index + 1}
                                </Badge>
                              )}
                              <span>{getSeniorityLabel(item.seniority_level)}</span>
                            </div>
                          </td>
                          <td className="text-center p-4">
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 font-semibold">
                              {item.min_region}
                            </Badge>
                          </td>
                          <td className="text-center p-4">
                            <span className="text-green-600 font-bold text-base">
                              ${item.min_rate.toFixed(2)}
                            </span>
                            <span className="text-slate-500 text-xs">/hr</span>
                          </td>
                          <td className="text-center p-4">
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300 font-semibold">
                              {item.max_region}
                            </Badge>
                          </td>
                          <td className="text-center p-4">
                            <span className="text-red-600 font-bold text-base">
                              ${item.max_rate.toFixed(2)}
                            </span>
                            <span className="text-slate-500 text-xs">/hr</span>
                          </td>
                          <td className="text-center p-4">
                            <div className="flex flex-col items-center">
                              <span className="font-bold text-purple-600 text-lg">
                                ${item.cost_savings.toFixed(2)}
                              </span>
                              <Badge className="bg-purple-100 text-purple-800 text-xs mt-1 px-2">
                                {item.savings_percentage.toFixed(0)}% savings
                              </Badge>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 border-t-2 border-indigo-200">
                        <td colSpan="5" className="p-4 text-right font-semibold text-slate-700">
                          Total Potential Annual Savings (assuming 2000 hrs/year):
                        </td>
                        <td className="text-center p-4">
                          <span className="font-bold text-purple-700 text-xl">
                            ${(optimizationData.comparison_table
                              .filter(item => item.cost_savings > 0)
                              .reduce((sum, item) => sum + item.cost_savings, 0) * 2000).toLocaleString()}
                          </span>
                          <div className="text-xs text-slate-600 mt-1">per year</div>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Summary Statistics */}
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <TrendingDown className="h-5 w-5 text-green-600" />
                      <h4 className="font-semibold text-green-900">Highest Savings</h4>
                    </div>
                    <p className="text-2xl font-bold text-green-700">
                      ${optimizationData.comparison_table
                        .filter(item => item.cost_savings > 0)
                        .sort((a, b) => b.cost_savings - a.cost_savings)[0]?.cost_savings.toFixed(2)}/hr
                    </p>
                    <p className="text-sm text-green-600 mt-1">
                      {getSeniorityLabel(optimizationData.comparison_table
                        .filter(item => item.cost_savings > 0)
                        .sort((a, b) => b.cost_savings - a.cost_savings)[0]?.seniority_level)}
                    </p>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <BarChart3 className="h-5 w-5 text-blue-600" />
                      <h4 className="font-semibold text-blue-900">Average Savings</h4>
                    </div>
                    <p className="text-2xl font-bold text-blue-700">
                      ${(optimizationData.comparison_table
                        .filter(item => item.cost_savings > 0)
                        .reduce((sum, item) => sum + item.cost_savings, 0) / 
                        optimizationData.comparison_table.filter(item => item.cost_savings > 0).length
                      ).toFixed(2)}/hr
                    </p>
                    <p className="text-sm text-blue-600 mt-1">
                      Across {optimizationData.comparison_table.filter(item => item.cost_savings > 0).length} roles
                    </p>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <DollarSign className="h-5 w-5 text-purple-600" />
                      <h4 className="font-semibold text-purple-900">Optimization Potential</h4>
                    </div>
                    <p className="text-2xl font-bold text-purple-700">
                      {(optimizationData.comparison_table
                        .filter(item => item.cost_savings > 0)
                        .reduce((sum, item) => sum + item.savings_percentage, 0) / 
                        optimizationData.comparison_table.filter(item => item.cost_savings > 0).length
                      ).toFixed(1)}%
                    </p>
                    <p className="text-sm text-purple-600 mt-1">
                      Average savings opportunity
                    </p>
                  </div>
                </div>

                {/* Actionable Insights */}
                <div className="mt-6 bg-gradient-to-r from-amber-50 to-orange-50 p-5 rounded-lg border-l-4 border-orange-500">
                  <h4 className="font-semibold text-orange-900 mb-3 flex items-center space-x-2">
                    <span>💡</span>
                    <span>Actionable Insights & Recommendations</span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-semibold text-orange-800 text-sm mb-2">Top 3 Cost-Saving Opportunities:</h5>
                      <ul className="text-sm text-orange-700 space-y-1">
                        {optimizationData.comparison_table
                          .filter(item => item.cost_savings > 0)
                          .sort((a, b) => b.cost_savings - a.cost_savings)
                          .slice(0, 3)
                          .map((item, idx) => (
                            <li key={idx}>
                              • <strong>{getSeniorityLabel(item.seniority_level)}</strong>: 
                              Hire from {item.min_region} instead of {item.max_region} 
                              (Save ${item.cost_savings.toFixed(2)}/hr)
                            </li>
                          ))}
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-semibold text-orange-800 text-sm mb-2">Quick Wins:</h5>
                      <ul className="text-sm text-orange-700 space-y-1">
                        <li>• Review current resource allocation against best regions</li>
                        <li>• Consider mixed-region teams for optimal cost-performance</li>
                        <li>• Negotiate contracts with insights from this comparison</li>
                        <li>• Plan future hiring based on regional cost advantages</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

const RiskAnalysis = () => {
  const [selectedScenario, setSelectedScenario] = useState('most_likely');
  const [calculations, setCalculations] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedDiscipline, setSelectedDiscipline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    projectLevel: true,
    disciplineLevel: false
  });

  useEffect(() => {
    fetchCalculations();
  }, []);

  const fetchCalculations = async () => {
    try {
      const response = await axios.get(`${API}/calculations`);
      setCalculations(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching calculations:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!selectedProject || !selectedProject.risk_scenarios || Object.keys(selectedProject.risk_scenarios).length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <BarChart3 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No Risk Analysis Available</h3>
          <p className="text-slate-500">Please calculate a project first to see risk analysis</p>
        </CardContent>
      </Card>
    );
  }

  const scenarios = selectedProject.risk_scenarios || {};
  const currentScenario = scenarios[selectedScenario] || null;

  const scenarioChartData = scenarios ? Object.entries(scenarios).map(([key, scenario]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1).replace('_', ' '),
    hours: scenario?.total_hours || 0,
    cost: scenario?.total_cost_usd || 0,
    confidence: scenario?.confidence_level || 0
  })) : [];

  const disciplineRiskData = (currentScenario && currentScenario.discipline_breakdown) ? currentScenario.discipline_breakdown.map((disc, index) => ({
    discipline: disc.discipline,
    hours: disc.total_hours,
    cost: disc.total_cost_usd,
    color: COLORS[index % COLORS.length]
  })) : [];

  return (
    <div className="space-y-8" id="risk-analysis">
      {/* Project & Discipline Selector */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Project Risk Analysis</CardTitle>
              <CardDescription>Analyze pessimistic, most likely, and optimistic scenarios for project and discipline outcomes</CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <span className="mr-2">🖨️</span> Print
              </Button>
              <Button variant="outline" size="sm" onClick={() => exportToPDF('risk-analysis', 'risk-analysis.pdf')}>
                <span className="mr-2">📄</span> Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col space-y-2">
              <Label>Select Project:</Label>
              <Select value={selectedProject?.id} onValueChange={(value) => {
                const project = calculations.find(p => p.id === value);
                setSelectedProject(project);
                setSelectedDiscipline(null);
              }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {calculations.map((calc) => (
                  <SelectItem key={calc.id} value={calc.id}>
                    {calc.project_name} - {new Date(calc.created_at).toLocaleDateString()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            </div>
            
            <div className="flex flex-col space-y-2">
              <Label>Filter by Discipline (Optional):</Label>
              <Select value={selectedDiscipline || 'all'} onValueChange={(value) => setSelectedDiscipline(value === 'all' ? null : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Disciplines" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Disciplines</SelectItem>
                  {selectedProject && currentScenario && currentScenario.discipline_breakdown && currentScenario.discipline_breakdown.map((disc) => (
                    <SelectItem key={disc.discipline} value={disc.discipline}>
                      {disc.discipline}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scenario Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
        {Object.entries(scenarios).map(([key, scenario]) => (
          <Card 
            key={key}
            className={`cursor-pointer transition-all hover:shadow-lg ${
              selectedScenario === key ? 'border-2 border-indigo-500 bg-indigo-50' : 'hover:border-indigo-300'
            }`}
            onClick={() => setSelectedScenario(key)}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold capitalize">
                  {key.replace('_', ' ')}
                </h3>
                <Badge 
                  variant={key === 'optimistic' ? 'success' : key === 'pessimistic' ? 'destructive' : 'default'}
                  className={
                    key === 'optimistic' ? 'bg-green-100 text-green-800' :
                    key === 'pessimistic' ? 'bg-red-100 text-red-800' :
                    'bg-blue-100 text-blue-800'
                  }
                >
                  {scenario.confidence_level}% Confidence
                </Badge>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-slate-600">Total Hours</p>
                  <p className="text-2xl font-bold text-slate-900">{scenario.total_hours.toFixed(1)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Total Cost</p>
                  <p className="text-2xl font-bold text-indigo-600">
                    ${scenario.total_cost_usd.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Risk Comparison Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Scenario Comparison</CardTitle>
          <CardDescription>Compare hours and costs across all risk scenarios</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <RechartsBarChart data={scenarioChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis yAxisId="left" orientation="left" tickFormatter={(value) => `${value.toFixed(0)}h`} />
              <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => `$${(value/1000).toFixed(0)}k`} />
              <Tooltip formatter={(value, name) => {
                if (name === 'cost') return [`$${value.toLocaleString()}`, 'Cost'];
                if (name === 'hours') return [`${value.toFixed(1)} hrs`, 'Hours'];
                return [value, name];
              }} />
              <Legend />
              <Bar yAxisId="left" dataKey="hours" fill="#8884d8" name="Hours" />
              <Bar yAxisId="right" dataKey="cost" fill="#82ca9d" name="Cost ($)" />
            </RechartsBarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Discipline Breakdown for Selected Scenario */}
      <Card>
        <CardHeader>
          <CardTitle className="capitalize">{selectedScenario.replace('_', ' ')} Scenario - Discipline Breakdown</CardTitle>
          <CardDescription>Detailed hours and cost analysis per discipline</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Hours Chart */}
            <div>
              <h4 className="text-sm font-semibold mb-4">Hours Distribution</h4>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPieChart>
                  <Pie
                    data={disciplineRiskData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ discipline, percent }) => `${discipline.substring(0, 10)} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="hours"
                  >
                    {disciplineRiskData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value.toFixed(1)} hrs`, 'Hours']} />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>

            {/* Cost Chart */}
            <div>
              <h4 className="text-sm font-semibold mb-4">Cost Distribution</h4>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPieChart>
                  <Pie
                    data={disciplineRiskData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ discipline, percent }) => `${discipline.substring(0, 10)} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#82ca9d"
                    dataKey="cost"
                  >
                    {disciplineRiskData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Cost']} />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Detailed Table */}
          <div className="mt-6 overflow-x-auto">
            <table className="w-full border-collapse text-sm min-w-[600px]">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="text-left p-3 font-semibold">Discipline</th>
                  <th className="text-right p-3 font-semibold">Hours</th>
                  <th className="text-right p-3 font-semibold">Cost</th>
                </tr>
              </thead>
              <tbody>
                {disciplineRiskData
                  .sort((a, b) => b.cost - a.cost)
                  .map((disc) => (
                    <tr key={disc.discipline} className="border-b hover:bg-slate-50/50">
                      <td className="p-3 font-medium">{disc.discipline}</td>
                      <td className="text-right p-3">{disc.hours.toFixed(1)} hrs</td>
                      <td className="text-right p-3 font-semibold text-indigo-600">
                        ${disc.cost.toLocaleString()}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Discipline-Level Risk Analysis */}
      {selectedDiscipline && scenarios && scenarios.pessimistic && scenarios.most_likely && scenarios.optimistic && (
        <>
          <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
                <span>Discipline Risk Analysis: {selectedDiscipline}</span>
              </CardTitle>
              <CardDescription>
                Detailed risk assessment with optimistic, most likely, and pessimistic scenarios
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Risk Grading */}
              {(() => {
                const pessimisticDisc = scenarios.pessimistic && scenarios.pessimistic.discipline_breakdown && scenarios.pessimistic.discipline_breakdown.find(d => d.discipline === selectedDiscipline);
                const mostLikelyDisc = scenarios.most_likely && scenarios.most_likely.discipline_breakdown && scenarios.most_likely.discipline_breakdown.find(d => d.discipline === selectedDiscipline);
                const optimisticDisc = scenarios.optimistic && scenarios.optimistic.discipline_breakdown && scenarios.optimistic.discipline_breakdown.find(d => d.discipline === selectedDiscipline);
                
                if (!pessimisticDisc || !mostLikelyDisc || !optimisticDisc) return null;
                
                // Calculate risk grade
                const costVariance = ((pessimisticDisc.total_cost_usd - optimisticDisc.total_cost_usd) / mostLikelyDisc.total_cost_usd) * 100;
                const hoursVariance = ((pessimisticDisc.total_hours - optimisticDisc.total_hours) / mostLikelyDisc.total_hours) * 100;
                
                let riskGrade = 'LOW';
                let riskColor = 'green';
                if (costVariance > 40 || hoursVariance > 40) {
                  riskGrade = 'HIGH';
                  riskColor = 'red';
                } else if (costVariance > 25 || hoursVariance > 25) {
                  riskGrade = 'MEDIUM';
                  riskColor = 'orange';
                }
                
                return (
                  <>
                    <div className="mb-6 p-4 bg-white rounded-lg border-2" style={{ borderColor: riskColor === 'red' ? '#ef4444' : riskColor === 'orange' ? '#f97316' : '#22c55e' }}>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-semibold">Risk Grade</h3>
                        <Badge 
                          className={`text-lg px-4 py-2 ${
                            riskGrade === 'HIGH' ? 'bg-red-100 text-red-800' :
                            riskGrade === 'MEDIUM' ? 'bg-orange-100 text-orange-800' :
                            'bg-green-100 text-green-800'
                          }`}
                        >
                          {riskGrade}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div className="bg-slate-50 p-3 rounded">
                          <p className="text-sm text-slate-600">Cost Variance Range</p>
                          <p className="text-2xl font-bold">{costVariance.toFixed(1)}%</p>
                        </div>
                        <div className="bg-slate-50 p-3 rounded">
                          <p className="text-sm text-slate-600">Hours Variance Range</p>
                          <p className="text-2xl font-bold">{hoursVariance.toFixed(1)}%</p>
                        </div>
                      </div>
                    </div>

                    {/* Three Scenarios Comparison */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-6">
                      {/* Optimistic */}
                      <Card className="border-2 border-green-300 bg-green-50">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center space-x-2">
                            <TrendingUp className="h-4 w-4 text-green-600" />
                            <span>Optimistic</span>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <div>
                              <p className="text-xs text-slate-600">Total Hours</p>
                              <p className="text-xl font-bold text-green-700">{optimisticDisc.total_hours.toFixed(1)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-600">Total Cost</p>
                              <p className="text-xl font-bold text-green-700">${optimisticDisc.total_cost_usd.toLocaleString()}</p>
                            </div>
                            <div className="pt-2 border-t">
                              <p className="text-xs text-slate-600">Engineer Hours</p>
                              <p className="text-sm font-semibold">{optimisticDisc.engineer_hours.toFixed(1)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-600">Technician Hours</p>
                              <p className="text-sm font-semibold">{optimisticDisc.technician_hours.toFixed(1)}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Most Likely */}
                      <Card className="border-2 border-blue-300 bg-blue-50">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center space-x-2">
                            <BarChart3 className="h-4 w-4 text-blue-600" />
                            <span>Most Likely</span>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <div>
                              <p className="text-xs text-slate-600">Total Hours</p>
                              <p className="text-xl font-bold text-blue-700">{mostLikelyDisc.total_hours.toFixed(1)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-600">Total Cost</p>
                              <p className="text-xl font-bold text-blue-700">${mostLikelyDisc.total_cost_usd.toLocaleString()}</p>
                            </div>
                            <div className="pt-2 border-t">
                              <p className="text-xs text-slate-600">Engineer Hours</p>
                              <p className="text-sm font-semibold">{mostLikelyDisc.engineer_hours.toFixed(1)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-600">Technician Hours</p>
                              <p className="text-sm font-semibold">{mostLikelyDisc.technician_hours.toFixed(1)}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Pessimistic */}
                      <Card className="border-2 border-red-300 bg-red-50">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center space-x-2">
                            <TrendingDown className="h-4 w-4 text-red-600" />
                            <span>Pessimistic</span>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <div>
                              <p className="text-xs text-slate-600">Total Hours</p>
                              <p className="text-xl font-bold text-red-700">{pessimisticDisc.total_hours.toFixed(1)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-600">Total Cost</p>
                              <p className="text-xl font-bold text-red-700">${pessimisticDisc.total_cost_usd.toLocaleString()}</p>
                            </div>
                            <div className="pt-2 border-t">
                              <p className="text-xs text-slate-600">Engineer Hours</p>
                              <p className="text-sm font-semibold">{pessimisticDisc.engineer_hours.toFixed(1)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-600">Technician Hours</p>
                              <p className="text-sm font-semibold">{pessimisticDisc.technician_hours.toFixed(1)}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Risk Mitigation Strategies */}
                    <Card className="border-2 border-purple-200">
                      <CardHeader>
                        <CardTitle className="flex items-center space-x-2">
                          <Shield className="h-5 w-5 text-purple-600" />
                          <span>Risk Mitigation Strategies & Cost Management</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {riskGrade === 'HIGH' && (
                            <>
                              <div className="bg-red-50 border-l-4 border-red-500 p-4">
                                <h4 className="font-semibold text-red-900 mb-2">⚠️ High Risk - Immediate Actions Required</h4>
                                <ul className="text-sm text-red-800 space-y-2 ml-4">
                                  <li>• <strong>Implement strict cost controls:</strong> Weekly budget reviews and variance analysis</li>
                                  <li>• <strong>Contingency reserves:</strong> Allocate additional {(costVariance * 0.3).toFixed(0)}% budget buffer</li>
                                  <li>• <strong>Resource optimization:</strong> Consider offshore/nearshore resources to reduce hourly rates</li>
                                  <li>• <strong>Scope management:</strong> Implement change control process to prevent scope creep</li>
                                  <li>• <strong>Early warning system:</strong> Set up automated alerts at 10% variance thresholds</li>
                                </ul>
                              </div>
                              <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                                <h4 className="font-semibold text-blue-900 mb-2">💡 Cost Management Recommendations</h4>
                                <ul className="text-sm text-blue-800 space-y-2 ml-4">
                                  <li>• <strong>Rebaseline project:</strong> Review deliverables and consider phased approach</li>
                                  <li>• <strong>Value engineering:</strong> Identify opportunities to reduce costs without compromising quality</li>
                                  <li>• <strong>Resource leveling:</strong> Balance workload across team to avoid overtime costs</li>
                                  <li>• <strong>Productivity improvement:</strong> Invest in automation and standardization tools</li>
                                  <li>• <strong>Estimated savings potential:</strong> ${(pessimisticDisc.total_cost_usd * 0.15).toLocaleString()} through optimization</li>
                                </ul>
                              </div>
                            </>
                          )}
                          
                          {riskGrade === 'MEDIUM' && (
                            <>
                              <div className="bg-orange-50 border-l-4 border-orange-500 p-4">
                                <h4 className="font-semibold text-orange-900 mb-2">⚡ Medium Risk - Monitoring & Control Needed</h4>
                                <ul className="text-sm text-orange-800 space-y-2 ml-4">
                                  <li>• <strong>Enhanced monitoring:</strong> Bi-weekly cost tracking and trend analysis</li>
                                  <li>• <strong>Contingency buffer:</strong> Maintain {(costVariance * 0.2).toFixed(0)}% reserve for uncertainties</li>
                                  <li>• <strong>Resource planning:</strong> Review resource allocation and skill mix monthly</li>
                                  <li>• <strong>Process optimization:</strong> Identify bottlenecks and streamline workflows</li>
                                  <li>• <strong>Stakeholder communication:</strong> Regular updates on cost performance</li>
                                </ul>
                              </div>
                              <div className="bg-green-50 border-l-4 border-green-500 p-4">
                                <h4 className="font-semibold text-green-900 mb-2">💡 Cost Optimization Opportunities</h4>
                                <ul className="text-sm text-green-800 space-y-2 ml-4">
                                  <li>• <strong>Standardization:</strong> Use templates and reusable components to reduce hours</li>
                                  <li>• <strong>Early issue detection:</strong> Implement quality gates to catch problems early</li>
                                  <li>• <strong>Efficient resource mix:</strong> Balance senior/junior ratio for optimal cost-performance</li>
                                  <li>• <strong>Estimated savings potential:</strong> ${(pessimisticDisc.total_cost_usd * 0.08).toLocaleString()} through best practices</li>
                                </ul>
                              </div>
                            </>
                          )}
                          
                          {riskGrade === 'LOW' && (
                            <>
                              <div className="bg-green-50 border-l-4 border-green-500 p-4">
                                <h4 className="font-semibold text-green-900 mb-2">✅ Low Risk - Maintain Current Trajectory</h4>
                                <ul className="text-sm text-green-800 space-y-2 ml-4">
                                  <li>• <strong>Standard monitoring:</strong> Monthly cost reviews are sufficient</li>
                                  <li>• <strong>Minimal contingency:</strong> {(costVariance * 0.1).toFixed(0)}% buffer recommended</li>
                                  <li>• <strong>Best practices:</strong> Continue current resource management approach</li>
                                  <li>• <strong>Documentation:</strong> Capture lessons learned for future projects</li>
                                  <li>• <strong>Continuous improvement:</strong> Look for incremental efficiency gains</li>
                                </ul>
                              </div>
                              <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                                <h4 className="font-semibold text-blue-900 mb-2">💡 Further Optimization Potential</h4>
                                <ul className="text-sm text-blue-800 space-y-2 ml-4">
                                  <li>• <strong>Knowledge sharing:</strong> Document efficient practices for organization-wide benefit</li>
                                  <li>• <strong>Innovation opportunities:</strong> Test new tools/methods with low risk exposure</li>
                                  <li>• <strong>Benchmark performance:</strong> Use as baseline for similar projects</li>
                                  <li>• <strong>Estimated additional savings:</strong> ${(mostLikelyDisc.total_cost_usd * 0.05).toLocaleString()} through fine-tuning</li>
                                </ul>
                              </div>
                            </>
                          )}

                          {/* Financial Impact Summary */}
                          <div className="bg-slate-100 p-4 rounded-lg">
                            <h4 className="font-semibold text-slate-900 mb-3">📊 Financial Impact Summary</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                              <div>
                                <p className="text-xs text-slate-600">Best Case (Optimistic)</p>
                                <p className="text-lg font-bold text-green-600">${optimisticDisc.total_cost_usd.toLocaleString()}</p>
                                <p className="text-xs text-green-600">Save: ${(mostLikelyDisc.total_cost_usd - optimisticDisc.total_cost_usd).toLocaleString()}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-600">Expected (Most Likely)</p>
                                <p className="text-lg font-bold text-blue-600">${mostLikelyDisc.total_cost_usd.toLocaleString()}</p>
                                <p className="text-xs text-slate-600">Target baseline</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-600">Worst Case (Pessimistic)</p>
                                <p className="text-lg font-bold text-red-600">${pessimisticDisc.total_cost_usd.toLocaleString()}</p>
                                <p className="text-xs text-red-600">Risk: +${(pessimisticDisc.total_cost_usd - mostLikelyDisc.total_cost_usd).toLocaleString()}</p>
                              </div>
                            </div>
                          </div>

                          {/* Action Items */}
                          <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                            <h4 className="font-semibold text-indigo-900 mb-3">🎯 Recommended Action Items</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div className="bg-white p-3 rounded border border-indigo-100">
                                <p className="text-sm font-semibold text-indigo-900">Immediate (This Week)</p>
                                <ul className="text-xs text-indigo-700 mt-2 space-y-1 ml-3">
                                  <li>• Review current budget vs. forecast</li>
                                  <li>• Identify cost-saving opportunities</li>
                                  <li>• Set up monitoring dashboards</li>
                                </ul>
                              </div>
                              <div className="bg-white p-3 rounded border border-indigo-100">
                                <p className="text-sm font-semibold text-indigo-900">Short-term (This Month)</p>
                                <ul className="text-xs text-indigo-700 mt-2 space-y-1 ml-3">
                                  <li>• Optimize resource allocation</li>
                                  <li>• Implement cost control measures</li>
                                  <li>• Update risk register</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

const ExecutiveSummary = () => {
  const [calculations, setCalculations] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    financial: true,
    risk: false,
    resource: false,
    commercial: false,
    assumptions: false,
    recommendations: false
  });

  useEffect(() => {
    fetchCalculations();
  }, []);

  const fetchCalculations = async () => {
    try {
      const response = await axios.get(`${API}/calculations`);
      setCalculations(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching calculations:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!selectedProject) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <TrendingUp className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No Executive Summary Available</h3>
          <p className="text-slate-500">Please calculate a project first to see the executive summary</p>
        </CardContent>
      </Card>
    );
  }

  const financial = selectedProject.financial_metrics;
  const progress = selectedProject.progress_metrics;
  const risks = selectedProject.risk_scenarios;

  return (
    <div className="space-y-6" id="commercial-summary">
      {/* Project Selector */}
      <Card className="border-2 border-indigo-200">
        <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center space-x-2">
                <TrendingUp className="h-6 w-6 text-indigo-600" />
                <span>Commercial Summary</span>
              </CardTitle>
              <CardDescription>Comprehensive one-page project overview with key metrics and insights</CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <span className="mr-2">🖨️</span> Print
              </Button>
              <Button variant="outline" size="sm" onClick={() => exportToPDF('commercial-summary', 'commercial-summary.pdf')}>
                <span className="mr-2">📄</span> Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex items-center space-x-4">
            <Label>Select Project:</Label>
            <Select value={selectedProject?.id} onValueChange={(value) => {
              const project = calculations.find(p => p.id === value);
              setSelectedProject(project);
            }}>
              <SelectTrigger className="w-[400px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {calculations.map((calc) => (
                  <SelectItem key={calc.id} value={calc.id}>
                    {calc.project_name} - {new Date(calc.created_at).toLocaleDateString()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Project Overview */}
      <Card>
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50"
          onClick={() => toggleSection('overview')}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <Building className="h-5 w-5 text-blue-600" />
              <span>📊 Project Overview</span>
            </CardTitle>
            <Badge>{expandedSections.overview ? '▼' : '▶'}</Badge>
          </div>
        </CardHeader>
        {expandedSections.overview && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-600 font-medium">Project Name</p>
                <p className="text-lg font-bold text-blue-900">{selectedProject.project_name}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-green-600 font-medium">Duration</p>
                <p className="text-lg font-bold text-green-900">{selectedProject.project_duration_months} months</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm text-purple-600 font-medium">% Complete</p>
                <p className="text-lg font-bold text-purple-900">{progress?.percent_complete || 0}%</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <p className="text-sm text-orange-600 font-medium">Status</p>
                <p className="text-lg font-bold text-orange-900">
                  {progress?.percent_complete === 100 ? 'Complete' : progress?.percent_complete > 0 ? 'In Progress' : 'Not Started'}
                </p>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Financial Summary */}
      <Card>
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50"
          onClick={() => toggleSection('financial')}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              <span>💰 Financial Summary</span>
            </CardTitle>
            <Badge>{expandedSections.financial ? '▼' : '▶'}</Badge>
          </div>
        </CardHeader>
        {expandedSections.financial && financial && (
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
              <div className="space-y-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-sm text-slate-600 mb-1">Total Project Cost</p>
                  <p className="text-2xl font-bold text-slate-900">
                    ${selectedProject.total_cost_usd.toLocaleString()}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-green-600 mb-1">Target Revenue</p>
                  <p className="text-2xl font-bold text-green-900">
                    ${financial.target_revenue.toLocaleString()}
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 mb-1">Expected Profit</p>
                  <p className="text-2xl font-bold text-blue-900">
                    ${financial.profit_at_completion.toLocaleString()}
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm text-purple-600 mb-1">Target ROI</p>
                  <p className="text-2xl font-bold text-purple-900">{financial.roi_percentage}%</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="bg-orange-50 p-4 rounded-lg">
                  <p className="text-sm text-orange-600 mb-1">Breakeven Point</p>
                  <p className="text-2xl font-bold text-orange-900">{financial.breakeven_percentage}%</p>
                </div>
                <div className="bg-indigo-50 p-4 rounded-lg">
                  <p className="text-sm text-indigo-600 mb-1">Required Margin</p>
                  <p className="text-2xl font-bold text-indigo-900">{financial.required_margin.toFixed(1)}%</p>
                </div>
              </div>
            </div>

            {progress && progress.percent_complete > 0 && (
              <div className="mt-6 pt-6 border-t">
                <h4 className="font-semibold mb-4">Progress-Based Financials</h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-slate-100 p-3 rounded">
                    <p className="text-xs text-slate-600">Actual Cost Spent</p>
                    <p className="text-lg font-bold text-slate-900">
                      ${progress.actual_cost_spent.toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-slate-100 p-3 rounded">
                    <p className="text-xs text-slate-600">Remaining Cost</p>
                    <p className="text-lg font-bold text-slate-900">
                      ${progress.remaining_cost.toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-slate-100 p-3 rounded">
                    <p className="text-xs text-slate-600">Projected Total</p>
                    <p className="text-lg font-bold text-slate-900">
                      ${progress.projected_total_cost.toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-slate-100 p-3 rounded">
                    <p className="text-xs text-slate-600">Cost Variance</p>
                    <p className={`text-lg font-bold ${progress.cost_variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${Math.abs(progress.cost_variance).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Risk Assessment */}
      <Card>
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50"
          onClick={() => toggleSection('risk')}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5 text-red-600" />
              <span>⚠️ Risk Assessment</span>
            </CardTitle>
            <Badge>{expandedSections.risk ? '▼' : '▶'}</Badge>
          </div>
        </CardHeader>
        {expandedSections.risk && risks && (
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
              {Object.entries(risks).map(([key, scenario]) => (
                <div 
                  key={key}
                  className={`p-4 rounded-lg border-2 ${
                    key === 'optimistic' ? 'bg-green-50 border-green-300' :
                    key === 'pessimistic' ? 'bg-red-50 border-red-300' :
                    'bg-blue-50 border-blue-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold capitalize">{key.replace('_', ' ')}</h4>
                    <Badge variant="outline">{scenario.confidence_level}%</Badge>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs text-slate-600">Cost Range</p>
                      <p className="text-lg font-bold">
                        ${scenario.total_cost_usd.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-600">Hours Range</p>
                      <p className="text-sm font-semibold">{scenario.total_hours.toFixed(0)} hrs</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <p className="text-sm font-semibold text-yellow-800 mb-1">Key Risk Factors</p>
              <ul className="text-sm text-yellow-700 space-y-1 ml-4">
                <li>• Cost variance between pessimistic and optimistic: ${(risks.pessimistic.total_cost_usd - risks.optimistic.total_cost_usd).toLocaleString()}</li>
                <li>• Schedule impact: {((risks.pessimistic.total_hours - risks.most_likely.total_hours) / risks.most_likely.total_hours * 100).toFixed(1)}% potential delay</li>
                <li>• Recommended contingency: {financial ? (financial.roi_percentage * 0.3).toFixed(1) : 10}%</li>
              </ul>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Key Assumptions */}
      <Card>
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50"
          onClick={() => toggleSection('assumptions')}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <Info className="h-5 w-5 text-slate-600" />
              <span>📝 Key Assumptions</span>
            </CardTitle>
            <Badge>{expandedSections.assumptions ? '▼' : '▶'}</Badge>
          </div>
        </CardHeader>
        {expandedSections.assumptions && (
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <h4 className="font-semibold mb-3">Project Parameters</h4>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between">
                    <span className="text-slate-600">Total Hours:</span>
                    <span className="font-semibold">{selectedProject.total_hours.toFixed(1)} hrs</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-slate-600">Project Duration:</span>
                    <span className="font-semibold">{selectedProject.project_duration_months} months</span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-slate-600">Disciplines:</span>
                    <span className="font-semibold">{selectedProject.discipline_breakdown ? selectedProject.discipline_breakdown.length : 0}</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold mb-3">Financial Assumptions</h4>
                <ul className="space-y-2 text-sm">
                  {financial && (
                    <>
                      <li className="flex justify-between">
                        <span className="text-slate-600">Target ROI:</span>
                        <span className="font-semibold">{financial.roi_percentage}%</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="text-slate-600">Breakeven Point:</span>
                        <span className="font-semibold">{financial.breakeven_percentage}% completion</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="text-slate-600">Required Margin:</span>
                        <span className="font-semibold">{financial.required_margin.toFixed(1)}%</span>
                      </li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Recommendations */}
      <Card className="border-2 border-green-200">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 bg-green-50"
          onClick={() => toggleSection('recommendations')}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              <span>✅ Recommendations for Maximum Profit</span>
            </CardTitle>
            <Badge>{expandedSections.recommendations ? '▼' : '▶'}</Badge>
          </div>
        </CardHeader>
        {expandedSections.recommendations && financial && (
          <CardContent>
            <div className="space-y-4">
              <div className="bg-green-50 border-l-4 border-green-500 p-4">
                <p className="font-semibold text-green-900 mb-2">💡 Revenue Optimization</p>
                <p className="text-sm text-green-800">
                  To achieve {financial.roi_percentage}% ROI, set project revenue at <strong>${financial.target_revenue.toLocaleString()}</strong>. 
                  This provides a profit margin of <strong>${financial.profit_at_completion.toLocaleString()}</strong>.
                </p>
              </div>
              
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                <p className="font-semibold text-blue-900 mb-2">📊 Cost Management</p>
                <p className="text-sm text-blue-800">
                  Monitor costs closely to stay within the baseline estimate of <strong>${selectedProject.total_cost_usd.toLocaleString()}</strong>. 
                  Any variance beyond 10% should trigger immediate review.
                </p>
              </div>
              
              <div className="bg-purple-50 border-l-4 border-purple-500 p-4">
                <p className="font-semibold text-purple-900 mb-2">⚠️ Risk Mitigation</p>
                <p className="text-sm text-purple-800">
                  Consider the pessimistic scenario cost of <strong>${risks?.pessimistic.total_cost_usd.toLocaleString()}</strong>. 
                  Build in contingency reserves and maintain flexible resource allocation.
                </p>
              </div>
              
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4">
                <p className="font-semibold text-orange-900 mb-2">🎯 Resource Strategy</p>
                <p className="text-sm text-orange-800">
                  Review the Regional Cost Optimization on the Rates tab to identify opportunities for cost savings 
                  through strategic resource allocation across regions.
                </p>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

const Dashboard = () => {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [activeTab, setActiveTab] = useState('calculator');
  const [mode, setMode] = useState(() => localStorage.getItem('appMode') || 'design');

  useEffect(() => { localStorage.setItem('appMode', mode); }, [mode]);

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const handleSubscription = () => {
    navigate('/subscription');
  };

  const tabs = [
    { value: 'calculator', label: 'Calculator', icon: Calculator },
    { value: 'commercial-summary', label: 'Commercial', icon: DollarSign },
    { value: 'risk-analysis', label: 'Risk', icon: TrendingUp },
    { value: 'project-analysis', label: 'Projects', icon: Building },
    { value: 'rates', label: 'Rates', icon: Users },
    { value: 'resource-optimization', label: 'Resources', icon: BarChart3 },
    { value: 'all-analytics', label: 'Analytics', icon: PieChart },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header - Mobile Responsive */}
      <div className="bg-background border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Logo & Title */}
            <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
              <div className="relative h-9 w-9 sm:h-10 sm:w-10 flex items-center justify-center border border-foreground bg-background flex-shrink-0">
                <div className="absolute inset-1 border border-foreground/40" />
                <span className="relative font-mono text-[10px] sm:text-[11px] font-bold text-emerald-glow">B.AI</span>
              </div>
              <div className="min-w-0">
                <h1 className="font-display text-sm sm:text-lg md:text-2xl font-bold text-foreground truncate tracking-tight">
                  <span className="hidden sm:inline">BuildMetri<span className="text-emerald-glow">.AI</span> <span className="text-muted-foreground font-normal">|</span> Project Lifecycle Cost Estimation Suite</span>
                  <span className="sm:hidden">BuildMetri<span className="text-emerald-glow">.AI</span></span>
                </h1>
                <p className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground hidden sm:block mt-0.5">AI Powered · Design &amp; Construction · Region-calibrated</p>
              </div>
            </div>

            {/* Desktop User Menu */}
            <div className="hidden md:flex items-center space-x-3">
              <NotificationBell />
              
              <div className="relative">
                <Button
                  variant="outline"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2"
                >
                  <User className="h-4 w-4" />
                  <span className="hidden lg:inline max-w-[150px] truncate">{userProfile?.name || currentUser?.email || 'User'}</span>
                  {userProfile?.subscription_status === 'trial' && (
                    <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-700 border-blue-300">
                      Trial: {userProfile?.trial_days_left || 0}d
                    </Badge>
                  )}
                  {userProfile?.subscription_status === 'active' && (
                    <Badge className="ml-2 bg-green-600">Premium</Badge>
                  )}
                </Button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50">
                    <div className="px-4 py-2 border-b border-slate-200">
                      <p className="text-sm font-semibold text-slate-900">{userProfile?.name || 'User'}</p>
                      <p className="text-xs text-slate-600">{currentUser?.email}</p>
                    </div>
                    
                    <button
                      onClick={handleSubscription}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center space-x-2"
                    >
                      <CreditCard className="h-4 w-4 text-slate-600" />
                      <span>Subscription</span>
                    </button>

                    <div className="border-t border-slate-200 my-1"></div>

                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-red-50 text-red-600 flex items-center space-x-2"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex md:hidden items-center space-x-2">
              <NotificationBell />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="p-2"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Mobile Menu Dropdown */}
          {showMobileMenu && (
            <div className="md:hidden mt-3 pt-3 border-t border-slate-200">
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-semibold text-slate-900">{userProfile?.name || 'User'}</p>
                  <p className="text-xs text-slate-600">{currentUser?.email}</p>
                </div>
                {userProfile?.subscription_status === 'trial' && (
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 text-xs">
                    Trial: {userProfile?.trial_days_left || 0} days
                  </Badge>
                )}
                {userProfile?.subscription_status === 'active' && (
                  <Badge className="bg-green-600 text-xs">Premium</Badge>
                )}
              </div>
              <div className="flex space-x-2 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSubscription}
                  className="flex-1 text-xs"
                >
                  <CreditCard className="h-3 w-3 mr-1" />
                  Subscription
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  className="flex-1 text-xs text-red-600 border-red-200 hover:bg-red-50"
                >
                  <LogOut className="h-3 w-3 mr-1" />
                  Logout
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Mode toggle: Design vs Construction */}
        <div className="mb-5 flex justify-center">
          <div className="inline-flex bg-white border border-slate-200 rounded-full p-1 shadow-sm" data-testid="mode-toggle">
            <button
              onClick={() => setMode('design')}
              className={`px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold inline-flex items-center gap-2 transition-all ${mode === 'design' ? 'bg-indigo-600 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid="mode-design"
            >
              <Hammer className="h-4 w-4" />
              <span>Design Phase</span>
            </button>
            <button
              onClick={() => setMode('construction')}
              className={`px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold inline-flex items-center gap-2 transition-all ${mode === 'construction' ? 'bg-orange-600 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid="mode-construction"
            >
              <HardHat className="h-4 w-4" />
              <span>Construction Phase</span>
            </button>
          </div>
        </div>

        {mode === 'construction' ? (
          <ConstructionShell />
        ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-8">
          {/* Mobile Tab Selector */}
          <div className="md:hidden">
            <Select value={activeTab} onValueChange={setActiveTab}>
              <SelectTrigger className="w-full">
                <SelectValue>
                  <div className="flex items-center space-x-2">
                    {tabs.find(t => t.value === activeTab)?.icon && (
                      <span>{React.createElement(tabs.find(t => t.value === activeTab).icon, { className: "h-4 w-4" })}</span>
                    )}
                    <span>{tabs.find(t => t.value === activeTab)?.label}</span>
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {tabs.map((tab) => (
                  <SelectItem key={tab.value} value={tab.value}>
                    <div className="flex items-center space-x-2">
                      <tab.icon className="h-4 w-4" />
                      <span>{tab.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Desktop Tabs */}
          <TabsList className="hidden md:grid w-full grid-cols-7 text-xs lg:text-sm">
            <TabsTrigger value="calculator" className="flex items-center justify-center space-x-1">
              <Calculator className="h-4 w-4" />
              <span className="hidden lg:inline">Calculator</span>
            </TabsTrigger>
            <TabsTrigger value="commercial-summary" className="flex items-center justify-center space-x-1">
              <DollarSign className="h-4 w-4" />
              <span className="hidden lg:inline">Commercial</span>
            </TabsTrigger>
            <TabsTrigger value="risk-analysis" className="flex items-center justify-center space-x-1">
              <TrendingUp className="h-4 w-4" />
              <span className="hidden lg:inline">Risk</span>
            </TabsTrigger>
            <TabsTrigger value="project-analysis" className="flex items-center justify-center space-x-1">
              <Building className="h-4 w-4" />
              <span className="hidden lg:inline">Projects</span>
            </TabsTrigger>
            <TabsTrigger value="rates" className="flex items-center justify-center space-x-1">
              <Users className="h-4 w-4" />
              <span className="hidden lg:inline">Rates</span>
            </TabsTrigger>
            <TabsTrigger value="resource-optimization" className="flex items-center justify-center space-x-1">
              <BarChart3 className="h-4 w-4" />
              <span className="hidden lg:inline">Resources</span>
            </TabsTrigger>
            <TabsTrigger value="all-analytics" className="flex items-center justify-center space-x-1">
              <PieChart className="h-4 w-4" />
              <span className="hidden lg:inline">Analytics</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="calculator">
            <ProjectCalculator />
          </TabsContent>

          <TabsContent value="commercial-summary">
            <ExecutiveSummary />
          </TabsContent>

          <TabsContent value="risk-analysis">
            <RiskAnalysis />
          </TabsContent>

          <TabsContent value="project-analysis">
            <ProjectAnalysis />
          </TabsContent>

          <TabsContent value="rates">
            <RatesManagement />
          </TabsContent>

          <TabsContent value="resource-optimization">
            <ResourceOptimization />
          </TabsContent>

          <TabsContent value="all-analytics">
            <AnalyticsDashboard />
          </TabsContent>
        </Tabs>
        )}
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <Dashboard />
    </div>
  );
}

export default App;